import React, { useEffect, useId, useMemo, useRef, useState } from 'react';
import { agentChangesOn } from './agentFieldChanges';
import ChangedField from './ChangedField';
import {
  LinkedPfdRef,
  LinkedPnidRef,
  PidDocument,
  PnidEquipment,
  PnidInstrument,
  PnidLinkedPfdStreamRef,
  PnidNode,
  DrawingValidation,
  FindingField,
  PnidWizardState,
  PnidAIData,
  NodePointCandidate,
  DRAIN_TYPES,
  getEquipmentInstrumentName,
  getInstrumentCategoryName,
  INSTRUMENT_LINE_SUBTYPE_LABELS,
  InstrumentLineSubtype,
  LineType,
  LINE_TYPE_LABELS,
  InstallationType,
  INSTALLATION_TYPE_LABELS,
  TracingType,
  TRACING_TYPE_LABELS,
  FlowProfile,
  FLOW_PROFILE_LABELS,
  FlowDirection,
  FLOW_DIRECTION_LABELS,
  PrimaryDirection,
  PRIMARY_DIRECTION_LABELS,
  RegisterEquipmentContext,
} from '../types';
import DomainSelect from './DomainSelect';
import RefCombobox from './RefCombobox';
import ValidationFindings, { FindingsList } from './ValidationFindings';
import { findingAnchorId, findingsAt } from './findingFields';
import { changedElementFindings, currentFindings } from './findingRows';
import { useDraftOnCommit, useDraftValidation, type DraftRequest } from '../hooks/useDraftValidation';
import {
  buildRefOptions, derivePnid, endAssetTag, InstrumentRef, LineEnd, lineTypeForForm, RefSelection, selectionForRef,
  selectionForTypedText, shownEndSheet,
} from '../pnid/entityRef';
import { EndSheetField } from './EndSheetField';
import LineNumberField from './LineNumberField';
import AutocompleteDropdown, { AutocompleteOption } from './AutocompleteDropdown';
import { isCompoundTag } from '../utils/compoundTag';
import CompoundSplitEditor from './CompoundSplitEditor';
import { getInstrumentPrimaryLabel } from '../utils/instrumentIdentity';
import { getInstrumentEffectiveContext } from '../utils/unifiedInstruments';
import SchemaFieldRenderer, { CustomRendererMap, CustomRendererProps } from './SchemaFieldRenderer';
import TagAutocompleteInput from './TagAutocompleteInput';
import RedundancySetSection from './RedundancySetSection';
import DocumentsSection from './DocumentsSection';
import { useFormSchema } from '../hooks/useFormSchemas';
import { useEquipmentTypes } from '../hooks/useEquipmentTypes';
import {
  describePrimaryDrawing,
  getEquipmentRegisterContext,
  ownedByAnotherDrawing,
  primaryDrawing,
  resolvePrimaryMark,
  useInvalidateRegisterContext,
  useRegisterContext,
  useTagRegisterContext,
} from '../hooks/useRegisterContext';
import { buildEquipmentEditHref } from '../utils/pnidDeepLink';
import DockFooter from '../pnid/dock/DockFooter';
import {
  addRowButtonStyle,
  footerDangerButtonStyle,
  openLinkedDocumentButtonStyle,
  removeRowButtonStyle,
  rowEditButtonStyle,
} from './assetFormButtonStyles';

export type PnidDialogKind = 'equipment' | 'node';

/** Ties the Domain row's label to its select, which is not nested inside it. */
const OWNER_DOMAIN_SELECT_ID = 'pnid-mark-owner-domain';

const LINE_END_FIELDS = {
  source: { ref: 'source_ref', pnid: 'source_pnid' },
  destination: { ref: 'dest_ref', pnid: 'dest_pnid' },
} as const satisfies Record<LineEnd, { ref: string; pnid: string }>;

const LINE_END_LABELS: Record<LineEnd, string> = { source: 'Source', destination: 'Destination' };

/** The field a drawn line's form focuses on open. */
type LineFocusTarget = LineEnd | 'line_no';

/** The Primary P&ID question, set apart from the fields the answer governs. */
const PRIMARY_SOURCE_PANEL_STYLE: React.CSSProperties = {
  padding: 10,
  background: 'var(--raven-surface-tier2)',
  border: '1px solid var(--raven-divider)',
  borderRadius: 6,
};

/**
 * Collapse space/hyphen/underscore variants of a tag, as the register's
 * `normalize_asset_tag` does — "LG 402", "LG-402" and "lg_402" all name the one
 * asset. Re-spelling a tag is therefore not renaming it.
 */
function normalizeTag(tag: string): string {
  return tag.trim().toUpperCase().replace(/[\s_-]+/g, '-').replace(/^-+|-+$/g, '');
}

interface PnidMarkDialogProps {
  kind: PnidDialogKind;
  pnidNoDefault?: string | null;
  activeEquipment?: PnidEquipment | null;
  activeNode?: PnidNode | null;
  equipmentInstruments?: PnidInstrument[];
  nodeInstruments?: PnidInstrument[];
  directNodeInstruments?: PnidInstrument[];
  nodes?: PnidNode[];  // For parent node selection in bifurcating lines
  equipments?: PnidEquipment[];  // Manually created equipment for dropdowns
  instruments?: InstrumentRef[];  // Unified instruments — for node source/dest selector's Instrument tab
  aiData?: PnidAIData | null;  // AI-extracted data for dropdowns
  currentDocument?: PidDocument | null;
  allDocuments?: PidDocument[];
  linkedDocumentStates?: Record<string, PnidWizardState>;
  onLoadLinkedDocumentState?: (docId: string) => Promise<void>;
  onOpenLinkedDocument?: (target: {
    docId: string;
    targetEquipmentId?: string | null;
    targetNodeId?: string | null;
    newTab?: boolean;
  }) => void;
  initialValues?: Record<string, any>;
  /**
   * The path the SME just drew, for the read-only row under the line's fields: how many
   * vertices, how many of them bound. A duct has none. Given only while the form saves a drawn
   * line, which opens on its first empty field.
   */
  drawnPath?: { pointCount: number; boundCount: number };
  diagramMode?: 'pid' | 'pfd';
  onSubmit: (values: Record<string, any>) => void;
  onCancel: () => void;
  // For marking existing items from AI prefill
  isMarkingExisting?: boolean;
  onRedrawPath?: () => void;        // Remove current line and start fresh redraw
  /** Deletes the saved line or equipment through the workspace's confirm. Shown only on an existing element. */
  onDelete?: () => void;
  // For node instrument sequence management (when kind === 'node')
  onEditInstrument?: (instrumentId: string) => void;
  onUnattachInstrument?: (instrumentId: string) => void;
  // For equipment instrument management (when kind === 'equipment')
  onEditEquipmentInstrument?: (instrumentId: string) => void;
  onUnattachEquipmentInstrument?: (instrumentId: string) => void;
  onReorderInstruments?: (reordered: PnidInstrument[]) => void;
  onAutoSequence?: (nodeId: string) => Promise<void>;
  onAutoAddBranches?: (nodeId: string) => Promise<{ created: number; skipped: number }>;
  branchCandidates?: NodePointCandidate[];
  onConfirmBranchCandidate?: (candidateId: string) => void;
  onIgnoreBranchCandidate?: (candidateId: string) => void;
  onHighlightEntity?: (id: string | null) => void;
  /** The drawing's findings. The line form lists its line's under the instrument list. */
  validation?: DrawingValidation;
  /**
   * The draft call for the values Save would hand to `onSubmit`: the body the element's Save
   * sends, and the validate-draft POST. Made on each field blur. Absent (a new element, a
   * version preview, a PFD form), the form shows the saved findings only.
   */
  draftRequestFor?: (values: Parameters<PnidMarkDialogProps['onSubmit']>[0]) => DraftRequest;
  /** Callback to enter instrument drawing mode (auto-attaches to the given context) */
  onMarkInstrument?: (contextType: 'equipment' | 'node', contextId: string) => void;
  /**
   * Splits the equipment being edited into the given assets: the first part
   * renames it, every later part becomes a new equipment on this drawing.
   * Supplied only while an existing P&ID equipment is open, which is also what
   * makes the compound-tag warning offer the split at all. Rejects with the
   * message to show the reviewer, including what the run managed to save.
   */
  onSplitCompoundEquipment?: (
    parts: Array<{ equipment_no: string; description: string }>,
  ) => Promise<void>;
  /**
   * The stored equipment type the split's new assets would inherit — the saved
   * value, not the one in this form, because that is what the split sends. The
   * create route admits reviewed types only, so an unreviewed one is caught here
   * rather than half-way through the split.
   */
  compoundSplitInheritedType?: string | null;
  readOnly?: boolean;
  /** Told whether the SME has edited the form: a field changed by hand, or text typed into a line end. */
  onDirtyChange?: (dirty: boolean) => void;
}


const inferDefaultContextualType = (_kind: PnidDialogKind, values: Record<string, any>) => {
  const rawText = `${values.instrument_text ?? ''} ${values.subtype ?? ''} ${values.instrument_type ?? ''}`.toUpperCase();
  const hasAny = (terms: string[]) => terms.some((t) => rawText.includes(t));

  if (hasAny(['HPV', 'HPD', 'HIGH POINT VENT', 'HIGHPOINT VENT'])) return 'HPV';
  if (hasAny(['LPD', 'LOW POINT DRAIN', 'LOWPOINT DRAIN'])) return 'LPD';
  if (hasAny(['DV', 'DRAIN VALVE', 'DRAIN'])) return 'DV';
  if (hasAny(['VV', 'VENT VALVE', 'VENT'])) return 'VV';
  if (hasAny(['BV', 'BYPASS'])) return 'BV';
  if (hasAny(['BK', 'BLOCK'])) return 'BK';
  if (hasAny(['BL', 'BLEED'])) return 'BL';
  // NRV / Check valve detection
  if (hasAny(['NRV', 'NON RETURN', 'CHECK VALVE', 'NON-RETURN'])) return 'NRV';

  if (hasAny(['ISOLATION', 'IV'])) return 'IV';

  const valveKeywords = [
    'GATE VALVE',
    'GLOBE VALVE',
    'BALL VALVE',
    'BUTTERFLY VALVE',
    'NEEDLE VALVE',
    'PLUG VALVE',
    'DIAPHRAGM VALVE',
    'PINCH VALVE',
    'ANGLE VALVE',
    'DAMPER',
    '3-WAY',
    '3 WAY',
    'Y-TYPE',
    'JACKETED',
  ];
  if (hasAny(valveKeywords)) return 'IV';

  if (values.instrument_type === 'Valve') return 'IV';

  return null;
};

const PnidMarkDialog: React.FC<PnidMarkDialogProps> = ({
  kind,
  pnidNoDefault,
  activeEquipment,
  activeNode,
  equipmentInstruments = [],
  nodeInstruments = [],
  directNodeInstruments = [],
  nodes = [],
  equipments = [],
  instruments = [],
  aiData = null,
  initialValues,
  drawnPath,
  diagramMode = 'pid',
  onSubmit,
  onCancel,
  isMarkingExisting = false,
  onRedrawPath,
  onDelete,
  onEditInstrument,
  onUnattachInstrument,
  onEditEquipmentInstrument,
  onUnattachEquipmentInstrument,
  onReorderInstruments,
  onAutoSequence,
  onAutoAddBranches,
  branchCandidates = [],
  onConfirmBranchCandidate,
  onIgnoreBranchCandidate,
  onHighlightEntity,
  validation,
  draftRequestFor,
  onMarkInstrument,
  onSplitCompoundEquipment,
  compoundSplitInheritedType = null,
  currentDocument = null,
  allDocuments = [],
  linkedDocumentStates = {},
  onLoadLinkedDocumentState,
  onOpenLinkedDocument,
  readOnly = false,
  onDirtyChange,
}) => {
  const [values, setValues] = useState<Record<string, any>>({});
  const isPfdMode = diagramMode === 'pfd';
  const pfdFormKind = isPfdMode ? (kind === 'equipment' ? 'pfd_equipment' : 'pfd_node') : null;
  const pfdSchema = useFormSchema(pfdFormKind);

  // Supplies the type options, and judges a stored value against the reviewed
  // list so an unreviewed one can be re-classified.
  const equipmentTypes = useEquipmentTypes();
  // Only a loaded taxonomy can prove a value is outside it. While the fetch is
  // pending or failed, every key looks unknown, and marking a valid one as
  // unreviewed would state something false about stored data.
  const isUnreviewedEquipmentType =
    equipmentTypes.loaded &&
    Boolean(values.equipment_type) &&
    !equipmentTypes.types.some((item) => item.key === values.equipment_type);

  // What the register knows about this drawing's equipment. Only the equipment
  // form reads it, so nothing is fetched while a node or a PFD is being edited.
  const registerContextDocId =
    !isPfdMode && kind === 'equipment' ? currentDocument?.id ?? null : null;
  const registerContext = useRegisterContext(registerContextDocId);
  const invalidateRegisterContext = useInvalidateRegisterContext();
  const editedEquipmentId: string =
    values._markExistingId || values.id || activeEquipment?.id || '';
  const elementRegisterEntry = getEquipmentRegisterContext(
    registerContext.context,
    editedEquipmentId,
  );
  // The tag this equipment was opened on, which is the one the element's own
  // register entry answers for.
  const openedEquipmentNoRef = useRef('');
  // The tag the mark on the form answers for. It starts as the opened tag and
  // moves with each settle, so leaving the field without changing the tag is
  // told apart from renaming the element onto another asset.
  const markedTagRef = useRef('');
  /**
   * The tag the reviewer has settled on, once it is no longer the one the dialog
   * opened with. The register is then asked about that tag instead of about the
   * element, so choosing a tag another drawing owns names that drawing before
   * anything is saved. Reverting to the opened tag drops back to the element.
   */
  const [settledEquipmentTag, setSettledEquipmentTag] = useState<string | null>(null);
  const tagRegisterContext = useTagRegisterContext(registerContextDocId, settledEquipmentTag);
  const handleEquipmentTagSettled = (tag: string) => {
    const settled = tag.trim();
    const openedTag = openedEquipmentNoRef.current.trim();
    setSettledEquipmentTag(settled && settled !== openedTag ? settled : null);
    // The reviewer left the field on the tag the mark already answers for, so
    // their answer stands — leaving a field is not restating it.
    if (normalizeTag(settled) === normalizeTag(markedTagRef.current)) return;
    markedTagRef.current = settled;
    // A primary mark approves "this drawing defines tag X", so settling on a
    // different tag leaves the claim answering for an asset nobody approved it
    // for. Drop it: claiming the new tag is a deliberate re-tick, and the panel
    // beside the box already names whichever drawing holds that tag now.
    // Settling back on the opened tag only clears the answer, and the element's
    // own stored mark takes over again through the resync effect below.
    primaryMarkTouchedRef.current = false;
    if (normalizeTag(settled) === normalizeTag(openedTag)) return;
    carriedPrimarySourceRef.current = false;
    setValues((prev) => ({ ...prev, is_primary_source: undefined }));
  };
  /**
   * The element is pointed at a tag other than the one it opened on. Its stored
   * mark answers for the tag it opened on, so nothing may adopt that mark while
   * this holds.
   */
  const equipmentTagRenamed =
    settledEquipmentTag !== null &&
    normalizeTag(settledEquipmentTag) !== normalizeTag(openedEquipmentNoRef.current);
  // While a settled tag is in force it is that tag's answer the panel reports,
  // including how far the read has got.
  const readingTagRegisterEntry = settledEquipmentTag !== null;
  const equipmentRegisterEntry = readingTagRegisterEntry
    ? tagRegisterContext.entry
    : elementRegisterEntry;
  const registerLoading = readingTagRegisterEntry
    ? tagRegisterContext.loading
    : registerContext.loading;
  const registerError = readingTagRegisterEntry
    ? tagRegisterContext.error
    : registerContext.error;
  // The account this drawing belongs to is what scopes the Domain picker, and the
  // drawing's own plant owns an asset that names no owner.
  const drawingDomainId = currentDocument?.domain_id ?? '';
  // The register is keyed by the tag as saved, so documents follow the stored element the
  // dialog edits. A create form names none, whatever equipment the canvas has selected.
  const savedEquipment = kind === 'equipment' && values._markExistingId
    ? equipments.find((equipment) => equipment.id === values._markExistingId)
    : undefined;
  const savedEquipmentTag = savedEquipment?.equipment_no?.trim() ?? '';
  const storedOwnerDomainId: string = values.owner_domain_id ?? '';

  // The mark is offered on saved equipment only — an unsaved mark has no asset
  // to be primary for.
  const showPrimarySourceBlock =
    !isPfdMode &&
    kind === 'equipment' &&
    Boolean(editedEquipmentId) &&
    (Boolean(registerContext.context) || registerLoading || Boolean(registerError));
  // Whether the reviewer has answered the checkbox in this dialog session. Until
  // they have, the register's mark is the newer statement — another drawing may
  // have taken the asset over since this one loaded.
  const primaryMarkTouchedRef = useRef(false);
  const isPrimarySourceChecked = resolvePrimaryMark(equipmentRegisterEntry, {
    liveToggle: primaryMarkTouchedRef.current ? values.is_primary_source === true : null,
    wizardMark: values.is_primary_source === true,
  });
  const canonicalFieldsLocked = ownedByAnotherDrawing(
    equipmentRegisterEntry,
    isPrimarySourceChecked,
  );
  // The checkbox is on screen and can be answered, so it governs the fields it
  // owns. While the register is still being read there is nothing to tick, and
  // locking the fields would trap the reviewer behind a control that is absent.
  const primaryMarkOffered = showPrimarySourceBlock && !registerLoading && !registerError;
  /**
   * Type, sub-type and description describe the asset itself, so only the drawing a
   * reviewer approved as its primary P&ID may state them. Publishing the wrong
   * asset costs more than publishing none, so the tick is required even when
   * this is the only drawing that shows the asset.
   */
  const assetFieldsLocked =
    canonicalFieldsLocked ||
    (primaryMarkOffered &&
      !isPrimarySourceChecked &&
      !equipmentRegisterEntry?.this_file_is_primary);
  const lockedEquipmentType = canonicalFieldsLocked
    ? equipmentRegisterEntry?.asset_type ?? ''
    : '';
  const lockedTypeIsOffList =
    Boolean(lockedEquipmentType) &&
    equipmentTypes.loaded &&
    !equipmentTypes.types.some((item) => item.key === lockedEquipmentType);
  // The sub-type list belongs to the type on show, which is the register's own
  // while the canonical fields are locked to another drawing.
  const equipmentSubtypeOptions = equipmentTypes.subtypesFor(
    canonicalFieldsLocked ? lockedEquipmentType : (values.equipment_type ?? ''),
  );

  // The mark is only unset for equipment that carried it; equipment that never
  // did is left alone rather than told it is not primary.
  const carriedPrimarySourceRef = useRef(false);
  // The register can be the only place the mark is recorded, for equipment
  // whose own field predates it, so its answer counts as having carried it.
  useEffect(() => {
    if (equipmentRegisterEntry?.this_file_is_primary) carriedPrimarySourceRef.current = true;
  }, [equipmentRegisterEntry]);

  // Prefer _targetNodeId from initialValues over the activeNode prop to avoid
  // a stale-state race where activeNode hasn't caught up when the dialog opens.
  const effectiveNode = useMemo(() => {
    const targetNodeId = initialValues?._targetNodeId;
    if (targetNodeId && nodes) {
      const found = nodes.find(n => n.id === targetNodeId);
      if (found) return found;
    }
    return activeNode ?? null;
  }, [initialValues?._targetNodeId, nodes, activeNode]);

  // The line's or the equipment's findings: each under its field (a line's instrument list, an
  // equipment's tag), and all in the Findings list at the end. While a draft stands they are the
  // draft's.
  const findingFormId = useId();
  const findingElementId = kind === 'node' ? effectiveNode?.id : kind === 'equipment' ? editedEquipmentId : undefined;
  // An element with no finding has no entry.
  const elementFindings = (findingElementId && validation?.elements[findingElementId]?.findings) || [];
  const { draft, refusal: draftRefusal, isValidating: draftValidating, requestDraft, discardDraft } = useDraftValidation(currentDocument?.id, Boolean(draftRequestFor), validation);
  const shownFindings = currentFindings(elementFindings, draft);
  const findingElements = { nodes: nodes ?? [], equipments: equipments ?? [], instruments: instruments ?? [] };
  const renderFieldFindings = (field: FindingField) => (
    <ValidationFindings findings={findingsAt(shownFindings, field)}
      elements={findingElements}
      onHighlightEntity={onHighlightEntity}
      isValidating={draftValidating} />
  );

  // The line being edited is left out of the Lines group so it cannot name itself.
  const editedLineId: string | null = isPfdMode ? effectiveNode?.id ?? null : values._markExistingId ?? null;
  const sourceRefGroups = useMemo(
    () => buildRefOptions({ equipments: equipments ?? [], instruments: instruments ?? [], nodes: nodes ?? [], aiData: aiData ?? null, currentNodeId: editedLineId, end: 'source' }),
    [equipments, instruments, nodes, aiData, editedLineId],
  );
  const destRefGroups = useMemo(
    () => buildRefOptions({ equipments: equipments ?? [], instruments: instruments ?? [], nodes: nodes ?? [], aiData: aiData ?? null, currentNodeId: editedLineId, end: 'destination' }),
    [equipments, instruments, nodes, aiData, editedLineId],
  );
  // The stored element this dialog edits, when it is one the agent wrote: each
  // field the agent moved carries a mark and its old value (ChangedField).
  const markedElement = kind === 'node' ? effectiveNode : kind === 'equipment' ? (activeEquipment ?? null) : null;
  const agentChanges = useMemo(
    () => (markedElement ? agentChangesOn(markedElement) : new Map()),
    [markedElement],
  );
  const agentChangeFor = (key: string) => agentChanges.get(key);
  const restoreField = (key: string) => setField(key, agentChanges.get(key)?.previous ?? null);
  /**
   * The end ref each sheet was last picked or edited for. The dialog opens on a stored sheet
   * as its ref's, and on the blank sheet of a line an SME has saved as its stored end's, so a
   * sheet the SME cleared reopens blank; an AI-identified line's blank sheet prefills. Once the
   * end changes, its sheet shows the prefill until the SME edits it.
   */
  const [sheetOwners, setSheetOwners] = useState<Record<LineEnd, string | null>>({ source: null, destination: null });
  // DECISION(2026-10-27, Abhishek): a blank stored sheet reopens blank only on a line an SME
  // has saved; an AI-identified line's blank sheet still prefills from its end.
  // Stored data writes "" for every unknown sheet, so on an AI line a blank means the AI did
  // not know it, while on a saved line it is what the SME saved.
  const savedLine = kind === 'node' && values._markExistingId
    ? (nodes ?? []).find((node) => node.id === values._markExistingId && node.status !== 'ai_identified')
    : undefined;
  const sheetOwnerOf = (end: LineEnd): string | null => {
    if (sheetOwners[end] !== null || !savedLine) return sheetOwners[end];
    const { ref, pnid } = LINE_END_FIELDS[end];
    return String(savedLine[pnid] ?? '').trim() ? null : String(savedLine[ref] ?? '');
  };
  // Text typed into an end and not picked. Save commits it as the selection it names, so Save
  // never drops what the field shows.
  const [endDrafts, setEndDrafts] = useState<Record<LineEnd, string>>({ source: '', destination: '' });
  const draftChange = (end: LineEnd) => (text: string) =>
    setEndDrafts((prev) => (prev[end] === text ? prev : { ...prev, [end]: text }));
  const [saveError, setSaveError] = useState<string | null>(null);
  /** The end as the SME sees it: the text typed and not yet picked, else the stored ref. */
  const endText = (end: LineEnd): string => endDrafts[end].trim() || String(values[LINE_END_FIELDS[end].ref] ?? '');
  const endGroups = (end: LineEnd) => (end === 'source' ? sourceRefGroups : destRefGroups);
  const shownSheet = (end: LineEnd): string => shownEndSheet({
    sheetOwner: sheetOwnerOf(end),
    endText: endText(end),
    storedSheet: String(values[LINE_END_FIELDS[end].pnid] ?? ''),
    groups: endGroups(end),
    ownPnidNo: pnidNoDefault,
    instruments: instruments ?? [],
  });
  const editSheet = (end: LineEnd) => (sheet: string) => {
    setSheetOwners((prev) => ({ ...prev, [end]: endText(end) }));
    setField(LINE_END_FIELDS[end].pnid, sheet);
  };
  const renderEndSheetField = (end: LineEnd, disabled?: boolean) => (
    <div style={{ marginTop: 8 }}>
      <div style={{ fontSize: 12, fontWeight: 500, marginBottom: 6 }}>P&amp;ID</div>
      <EndSheetField
        label={end === 'source' ? 'Source P&ID' : 'Destination P&ID'}
        docId={currentDocument?.id ?? ''}
        assetTag={endAssetTag(selectionForRef(endText(end), endGroups(end)), instruments ?? [])}
        ownPnidNo={pnidNoDefault ?? ''}
        value={shownSheet(end)}
        onChange={editSheet(end)}
        disabled={disabled}
      />
    </div>
  );
  // The restored ref is text the agent replaced; the entity it named is not known here.
  const restoreRef = (refKey: 'source_ref' | 'dest_ref', idKey: 'source_id' | 'dest_id') => {
    restoreField(refKey);
    setField(idKey, null);
  };
  // One pick fills the end's ref, its bound entity's id (null for text alone) and its
  // sheet. drain_type is the picked drain's code, and nothing for any other pick: the
  // pick's kind decides, never the text.
  const endPatch = (end: LineEnd, selection: RefSelection): Record<string, unknown> => (end === 'source'
    ? {
      source_ref: selection.ref,
      source_id: selection.id ?? null,
      source_pnid: derivePnid(selection, pnidNoDefault, instruments ?? []),
    }
    : {
      dest_ref: selection.ref,
      dest_id: selection.id ?? null,
      drain_type: selection.kind === 'drain' ? selection.ref : null,
      dest_pnid: derivePnid(selection, pnidNoDefault, instruments ?? []),
    });
  // The pick replaces the end's typed text in the same render, so nothing that reads the form
  // after the pick (Save, the draft) commits the search text in place of the picked end.
  const pickEnd = (end: LineEnd) => (selection: RefSelection) => {
    setSheetOwners((prev) => ({ ...prev, [end]: selection.ref }));
    setEndDrafts((prev) => (prev[end] === '' ? prev : { ...prev, [end]: '' }));
    for (const [key, value] of Object.entries(endPatch(end, selection))) setField(key, value);
  };
  const pickSourceRef = pickEnd('source');
  const pickDestRef = pickEnd('destination');
  /**
   * DECISION(2026-10-26, Abhishek): A drawn P&ID line opens on its first empty field in the form's order (Line Number, Source, Destination), and on Line Number when all three are set.
   * The SME names the line before its ends, and focus that follows the visual order keeps Tab moving down the form.
   * A PFD stream's schema form has no Line Number field, so it opens on its first empty end.
   */
  // Read once on open; a later edit does not move the focus again.
  const [initialLineFocus] = useState<LineFocusTarget | null>(() => {
    if (kind !== 'node' || !drawnPath) return null;
    const focusOrder: LineFocusTarget[] = isPfdMode ? ['source', 'destination'] : ['line_no', 'source', 'destination'];
    const fieldKey = (target: LineFocusTarget) => (target === 'line_no' ? 'line_no' : LINE_END_FIELDS[target].ref);
    const firstEmpty = focusOrder.find((target) => !String(initialValues?.[fieldKey(target)] ?? '').trim());
    return firstEmpty ?? (isPfdMode ? null : 'line_no');
  });
  const lineNumberInputRef = useRef<HTMLInputElement>(null);
  useEffect(() => {
    if (initialLineFocus !== 'line_no') return;
    const timer = setTimeout(() => lineNumberInputRef.current?.focus(), 0);
    return () => clearTimeout(timer);
  }, [initialLineFocus]);

  // Instrument sequence management state (for kind === 'node')
  const [draggedIndex, setDraggedIndex] = useState<number | null>(null);
  const [dragOverIndex, setDragOverIndex] = useState<number | null>(null);
  const [confirmUnattach, setConfirmUnattach] = useState<string | null>(null);
  const [isAutoSequencing, setIsAutoSequencing] = useState(false);
  const [isDetectingBranches, setIsDetectingBranches] = useState(false);
  // Equipment instrument management state (for kind === 'equipment')
  const [confirmUnattachEquip, setConfirmUnattachEquip] = useState<string | null>(null);

  // Line Properties section (kind === 'node') — expanded if any field is non-default.
  const [linePropsExpanded, setLinePropsExpanded] = useState(false);

  useEffect(() => {
    const nextValues = initialValues ? { ...initialValues } : {};
    if (diagramMode === 'pfd') {
      const sourceDetails = nextValues.details && typeof nextValues.details === 'object' ? nextValues.details : {};
      const sourceLineSpec = nextValues.line_spec && typeof nextValues.line_spec === 'object' ? nextValues.line_spec : {};
      for (const [key, value] of Object.entries(sourceDetails)) {
        if (nextValues[`details.${key}`] === undefined) nextValues[`details.${key}`] = value;
      }
      for (const [key, value] of Object.entries(sourceLineSpec)) {
        if (nextValues[`line_spec.${key}`] === undefined) nextValues[`line_spec.${key}`] = value;
      }
    }
    setValues(nextValues);
    setSheetOwners({
      source: String(nextValues.source_pnid ?? '').trim() ? String(nextValues.source_ref ?? '') : null,
      destination: String(nextValues.dest_pnid ?? '').trim() ? String(nextValues.dest_ref ?? '') : null,
    });
    carriedPrimarySourceRef.current = nextValues.is_primary_source === true;
    primaryMarkTouchedRef.current = false;
    openedEquipmentNoRef.current =
      typeof nextValues.equipment_no === 'string' ? nextValues.equipment_no : '';
    markedTagRef.current = openedEquipmentNoRef.current;
    setSettledEquipmentTag(null);
    // Expand Line Properties if any non-default field is set on the active node.
    if (kind === 'node') {
      const hasNonDefault =
        Boolean(nextValues.painting_spec) ||
        (nextValues.installation_type && nextValues.installation_type !== 'above_ground_main') ||
        nextValues.is_jacketed === true ||
        (nextValues.tracing_type && nextValues.tracing_type !== 'none') ||
        (nextValues.flow_profile && nextValues.flow_profile !== 'standard') ||
        Boolean(nextValues.slope_ratio) ||
        nextValues.is_two_phase === true ||
        (nextValues.flow_direction && nextValues.flow_direction !== 'forward') ||
        Boolean(nextValues.primary_direction);
      setLinePropsExpanded(Boolean(hasNonDefault));
    }
  }, [initialValues, kind, diagramMode]);

  /**
   * Take the register's mark for this element as the form's own. The saved mark
   * is what the drawing holds, so the field states a claim only while one is
   * stored, and spelling out a release is left to the reviewer's untick.
   */
  const adoptSavedPrimaryMark = (savedMark: boolean) => {
    carriedPrimarySourceRef.current = savedMark;
    setValues((prev) => ({ ...prev, is_primary_source: savedMark ? true : undefined }));
  };

  // The register is the current word on what this drawing's annotation holds, so
  // a form the reviewer has not answered follows it: a sibling drawing that took
  // the asset over released this element's mark, and a save from here must carry
  // that release rather than the claim the dialog was seeded with. It is the
  // element's own entry that answers, since that is the mark the save writes —
  // a tag the reviewer is trying out is reported, not adopted.
  //
  // A renamed element is the one case the stored mark cannot answer: it approves
  // the tag the dialog opened on, not the one now typed. Reverting the tag ends
  // the rename and this restores the element's own stored state.
  useEffect(() => {
    if (primaryMarkTouchedRef.current || equipmentTagRenamed) return;
    const savedMark = elementRegisterEntry?.this_drawing_mark;
    if (savedMark === undefined || savedMark === null) return;
    adoptSavedPrimaryMark(savedMark);
    // These dependencies are picked by hand. `adoptSavedPrimaryMark` stays out:
    // it is rebuilt on every render and calls `setValues` with a new object, so
    // listing it would re-run this effect for as long as the dialog is open.
  }, [
    editedEquipmentId,
    equipmentTagRenamed,
    elementRegisterEntry?.this_drawing_mark,
  ]);


  useEffect(() => {
    if (values.contextual_type) return;
    const inferred = inferDefaultContextualType(kind, values);
    if (!inferred) return;
    setValues((prev) => ({ ...prev, contextual_type: inferred }));
  }, [kind, values.instrument_text, values.subtype, values.instrument_type, values.contextual_type]);

  // The action row renders in the dock's footer, outside the form's DOM.
  const formId = useId();

  // Track field change counts for analytics. The dialog mounts once per opening,
  // so the refs' initial values are already the per-opening starting point.
  const fieldChangeCountRef = useRef(0);
  const fieldsChangedRef = useRef<Set<string>>(new Set());

  // Stable custom-renderer maps for the PFD forms. Each entry delegates to a ref
  // whose `.current` is reassigned below (after setField / render helpers are defined),
  // so the renderer component identities never change — inputs keep focus on re-render.
  const pfdSourceRefRenderRef = useRef<(p: CustomRendererProps) => React.ReactNode>(() => null);
  const pfdDestRefRenderRef = useRef<(p: CustomRendererProps) => React.ReactNode>(() => null);
  const pfdConnectedEqRenderRef = useRef<() => React.ReactNode>(() => null);
  const pfdLinkedPnidRenderRef = useRef<() => React.ReactNode>(() => null);
  const pfdLinkedStreamRenderRef = useRef<() => React.ReactNode>(() => null);
  const pfdNodeCustomRenderers = useMemo<CustomRendererMap>(() => ({
    source_ref: (p) => pfdSourceRefRenderRef.current(p),
    dest_ref: (p) => pfdDestRefRenderRef.current(p),
    // The stored schema lists a sheet field per end. Each end's picker renders its P&ID
    // field beneath it, so the schema's own sheet inputs render nothing.
    source_pnid: () => null,
    dest_pnid: () => null,
    linked_pfd_streams: () => pfdLinkedStreamRenderRef.current(),
  }), []);
  // Read through a ref so the renderer map below keeps its empty dep list.
  const currentDocIdRef = useRef<string | null>(null);
  currentDocIdRef.current = currentDocument?.id ?? null;
  const pfdEquipmentCustomRenderers = useMemo<CustomRendererMap>(() => ({
    // The PFD equipment tag is the same plant vocabulary as the P&ID one, so the
    // schema's tag field renders the suggesting input rather than a plain text box.
    equipment_no: ({ field, value, onChange, readOnly: fieldReadOnly }) => (
      <label style={{ display: 'block', fontSize: 12 }}>
        <span style={{ fontWeight: 500 }}>
          {field.label}{field.required ? ' (required)' : ' (optional)'}
        </span>
        <TagAutocompleteInput
          value={typeof value === 'string' ? value : ''}
          onChange={onChange}
          docId={currentDocIdRef.current}
          kind="equipment"
          placeholder={field.placeholder}
          disabled={fieldReadOnly}
          required={field.required}
          hyphenateSpacedTag
          style={{ marginTop: 4 }}
        />
      </label>
    ),
    connected_equipments: () => pfdConnectedEqRenderRef.current(),
    linked_pnids: () => pfdLinkedPnidRenderRef.current(),
  }), []);

  useEffect(() => {
    if (!onLoadLinkedDocumentState) return;
    const rows = [
      ...(Array.isArray(values.linked_pnids) ? values.linked_pnids : []),
      ...(Array.isArray(values.linked_pfds) ? values.linked_pfds : []),
      ...(Array.isArray(values.linked_pfd_streams) ? values.linked_pfd_streams : []),
    ];
    for (const row of rows) {
      const targetFileId = row?.pnid_file_id || row?.pfd_file_id;
      if (targetFileId && !linkedDocumentStates[targetFileId]) {
        void onLoadLinkedDocumentState(targetFileId);
      }
    }
  }, [
    kind,
    linkedDocumentStates,
    onLoadLinkedDocumentState,
    values.linked_pfds,
    values.linked_pfd_streams,
    values.linked_pnids,
  ]);

  const setField = (key: string, value: any) => {
    fieldChangeCountRef.current += 1;
    fieldsChangedRef.current.add(key);
    setValues((prev) => ({ ...prev, [key]: value }));
  };

  const lineTypeFollowsEnds = kind === 'node' && !isPfdMode && !values._markExistingId && !fieldsChangedRef.current.has('line_type');
  const shownLineType = lineTypeForForm({
    chosen: values.line_type as LineType | undefined,
    followsEnds: lineTypeFollowsEnds,
    source: selectionForRef(endText('source'), sourceRefGroups),
    destination: selectionForRef(endText('destination'), destRefGroups),
  });

  // Edited: a field went through setField since the form opened, or text sits typed
  // into a line end. Values the form adjusts by itself (an inferred contextual type, a
  // resynced primary mark) are not the SME's edits, so they do not count. Each of
  // those writes re-renders, so the render reads the ref current.
  const formEdited = fieldsChangedRef.current.size > 0 || Boolean(endDrafts.source.trim() || endDrafts.destination.trim());
  useEffect(() => {
    onDirtyChange?.(formEdited);
  }, [formEdited, onDirtyChange]);

  /* The new type brings its own sub-type list, and a stored sub-type it does not
     offer would otherwise be saved against a list that no longer holds it. */
  const changeEquipmentType = (nextType: string) => {
    setField('equipment_type', nextType);
    if (values.subtype && !equipmentTypes.subtypesFor(nextType).includes(values.subtype)) {
      setField('subtype', null);
    }
  };

  /** The values Save hands to `onSubmit`, or why Save refuses the form as it stands. */
  const buildSubmitValues = (): { values: Parameters<typeof onSubmit>[0] } | { refusal: string } => {
    const committedEnds: Record<string, unknown> = {};
    if (kind === 'node') {
      for (const end of ['source', 'destination'] as const) {
        const typed = endDrafts[end].trim();
        if (!typed) continue;
        const selection = selectionForTypedText(typed, endGroups(end));
        if (!selection) {
          return { refusal: `${LINE_END_LABELS[end]}: "${typed}" is not a line on this sheet. Mark it first, or clear the field.` };
        }
        Object.assign(committedEnds, endPatch(end, selection));
      }
    }
    const savedValues: Record<string, unknown> = { ...values, ...committedEnds, ...(lineTypeFollowsEnds ? { line_type: shownLineType } : {}) };
    // DECISION(2026-10-27, Abhishek): A process line saves only with a Line Number; an instrument or tapping line may leave it blank.
    // The save generates an instrument line's number from its ends and signal, and a tapping line often carries no number on the drawing, so it saves blank.
    // Nothing names a process line without one. A PFD stream's Stream No is required by its own schema.
    const isProcessLine = ((savedValues.line_type as LineType | undefined) ?? 'process') === 'process';
    if (kind === 'node' && !isPfdMode && isProcessLine && !String(savedValues.line_no ?? '').trim()) {
      return { refusal: 'Enter a Line Number: a process line needs one to save.' };
    }
    // A line saves each end's sheet as its field shows it.
    const endSheets = kind === 'node' ? { source_pnid: shownSheet('source'), dest_pnid: shownSheet('destination') } : {};
    return {
      values: {
        ...savedValues,
        ...endSheets,
        _field_change_count: fieldChangeCountRef.current,
        _fields_changed: Array.from(fieldsChangedRef.current),
      },
    };
  };

  const handleSubmit = (e?: React.FormEvent) => {
    e?.preventDefault();
    if (readOnly) return;
    const submission = buildSubmitValues();
    if ('refusal' in submission) {
      setSaveError(submission.refusal);
      return;
    }
    setSaveError(null);
    onSubmit(submission.values);
  };

  // A draft validates the body Save would send now, so a form Save would refuse sends none, and
  // an unedited form shows the saved findings.
  const validateFormDraft = () => {
    if (!draftRequestFor || readOnly) return;
    if (!formEdited) {
      discardDraft();
      return;
    }
    const submission = buildSubmitValues();
    if ('refusal' in submission) return;
    requestDraft(draftRequestFor(submission.values));
  };
  const draftCommitHandlers = useDraftOnCommit(JSON.stringify(values), validateFormDraft);


  const formatLinkStatus = (status?: string | null) => (
    status === 'needs_review' ? 'Needs review' : 'Confirmed'
  );

  const formatStoredText = (value?: string | null) => (
    typeof value === 'string' && value.trim() ? value.trim() : '-'
  );

  const isOpcInstrument = (inst: PnidInstrument): boolean => (
    typeof inst.instrument_type === 'string' && inst.instrument_type.trim().toUpperCase() === 'OPC'
  );

  const getNodeOpcSnapshot = (
    docInstruments: PnidInstrument[],
    nodeId: string | null | undefined,
  ): { instrumentId: string | null; tag: string | null } => {
    if (!nodeId) return { instrumentId: null, tag: null };
    const seen = new Set<string>();
    const candidates = docInstruments
      .filter((inst) => {
        if (!inst.id || seen.has(inst.id) || !isOpcInstrument(inst)) return false;
        seen.add(inst.id);
        const effectiveContext = getInstrumentEffectiveContext(inst, { instruments: docInstruments });
        return effectiveContext.context_type === 'node' && effectiveContext.context_id === nodeId;
      })
      .sort((a, b) => getInstrumentPrimaryLabel(a).localeCompare(getInstrumentPrimaryLabel(b)));
    const selected = candidates[0];
    return selected
      ? { instrumentId: selected.id, tag: getInstrumentPrimaryLabel(selected) || selected.generated_id || null }
      : { instrumentId: null, tag: null };
  };

  // (Instrument-specific isolation valve, auto-ID, and linked instrument logic removed — now in InstrumentEditor)

  // Recursive value renderer — shows nested dicts/arrays as readable rows rather than raw JSON.
  const formatKey = (k: string) => k.replace(/_/g, ' ');

  const renderValue = (value: any, depth = 0): React.ReactNode => {
    if (value === null || value === undefined || value === '') {
      return <span style={{ color: 'var(--raven-text-disabled)' }}>—</span>;
    }
    if (typeof value === 'boolean') {
      return <span style={{ fontWeight: 500 }}>{value ? 'Yes' : 'No'}</span>;
    }
    if (typeof value === 'number' || typeof value === 'string') {
      return <span style={{ fontWeight: 500 }}>{String(value)}</span>;
    }
    if (Array.isArray(value)) {
      if (value.length === 0) return <span style={{ color: 'var(--raven-text-disabled)' }}>(empty)</span>;
      const allPrimitive = value.every(
        (v) => v === null || typeof v === 'string' || typeof v === 'number' || typeof v === 'boolean'
      );
      if (allPrimitive) {
        return <span style={{ fontWeight: 500 }}>{value.map((v) => String(v ?? '—')).join(', ')}</span>;
      }
      if (depth >= 3) {
        return <span style={{ color: 'var(--raven-text-disabled)', fontStyle: 'italic' }}>[{value.length} items]</span>;
      }
      return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
          {value.map((v, i) => (
            <div
              key={i}
              style={{
                padding: 4,
                borderLeft: '2px solid var(--raven-divider)',
                paddingLeft: 6,
              }}
            >
              {renderValue(v, depth + 1)}
            </div>
          ))}
        </div>
      );
    }
    // object
    const entries = Object.entries(value as Record<string, any>);
    if (entries.length === 0) return <span style={{ color: 'var(--raven-text-disabled)' }}>(empty)</span>;
    if (depth >= 3) {
      return <span style={{ color: 'var(--raven-text-disabled)', fontStyle: 'italic' }}>{`{${entries.length} fields}`}</span>;
    }
    return (
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'max-content 1fr',
          gap: '2px 8px',
          padding: depth === 0 ? 0 : 4,
          ...(depth > 0
            ? {
                borderLeft: '2px solid var(--raven-divider)',
                paddingLeft: 6,
              }
            : {}),
        }}
      >
        {entries.map(([k, v]) => (
          <React.Fragment key={k}>
            <span style={{ color: 'var(--raven-text-secondary)' }}>{formatKey(k)}:</span>
            <span>{renderValue(v, depth + 1)}</span>
          </React.Fragment>
        ))}
      </div>
    );
  };

  const renderDictSection = (title: string, data: Record<string, any> | null | undefined, accent: string) => {
    if (!data || typeof data !== 'object' || Object.keys(data).length === 0) return null;
    return (
      <div style={{ marginTop: 8 }}>
        <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--raven-text-secondary)', marginBottom: 6 }}>{title}</div>
        <div
          style={{
            padding: 8,
            background: accent,
            border: '1px solid var(--raven-divider)',
            borderRadius: 6,
            fontSize: 11,
          }}
        >
          {renderValue(data, 0)}
        </div>
      </div>
    );
  };

  // Render dynamic details + equipment_data for equipment
  const renderDetailsFields = () => (
    <>
      {renderDictSection('Design Data (from AI)', values.details, 'var(--raven-surface-tier2)')}
      {renderDictSection('Datasheet', values.equipment_data, 'var(--raven-surface-tier2)')}
    </>
  );

  /**
   * Checking the box claims the asset for this drawing; unchecking releases it,
   * but only for equipment that carried the mark — for the rest the field stays
   * undefined so the save says nothing about primacy.
   *
   * Either answer is the reviewer's own, so from here on it stands over what the
   * register reports, until the dialog reseeds or a refresh resyncs it.
   */
  const setPrimarySource = (checked: boolean) => {
    primaryMarkTouchedRef.current = true;
    if (checked) {
      setField('is_primary_source', true);
      return;
    }
    setField('is_primary_source', carriedPrimarySourceRef.current ? false : undefined);
  };

  /**
   * Which drawing is primary for this asset. Another drawing is a link: it opens
   * that drawing in a new tab with the same equipment already open, so the
   * reviewer can read or change the values it owns without hunting for it.
   */
  const renderPrimaryDrawing = (entry: RegisterEquipmentContext) => {
    const primary = primaryDrawing(entry, isPrimarySourceChecked);
    if (!primary) return 'none';
    if (primary.isThisDrawing) return 'this drawing';
    const label = primary.drawingNo || 'another drawing';
    if (!primary.docId) return label;
    return (
      <a
        href={buildEquipmentEditHref(primary.docId, primary.equipmentId)}
        target="_blank"
        rel="noreferrer"
        style={{ color: 'var(--raven-text-secondary)', fontWeight: 600 }}
      >
        {label}
      </a>
    );
  };

  // The mark that makes this drawing authoritative for the asset, over the
  // register's account of every drawing the asset appears on.
  const renderPrimarySourceBlock = () => {
    if (!showPrimarySourceBlock) return null;

    // Cross-drawing data (Primary/Also-appears-on, and locked fields) is only
    // as fresh as the last fetch, so a reviewer who just edited the asset on
    // another tab can force this drawing to reread the register rather than
    // waiting on a full page reload.
    //
    // Refreshing is a resync, so the saved state wins outright: an unsaved tick
    // is dropped here, and the reread that follows reseeds the form from what
    // the register reports.
    const refreshRegisterContext = () => {
      if (!registerContextDocId) return;
      primaryMarkTouchedRef.current = false;
      // The element's own entry, since that is the mark a save writes — a tag
      // the reviewer is trying out is reported, not adopted. A renamed element
      // has no stored mark that answers for the tag it now carries, so the
      // resync leaves the dropped claim dropped.
      const savedMark = elementRegisterEntry?.this_drawing_mark;
      if (!equipmentTagRenamed && savedMark !== undefined && savedMark !== null) {
        adoptSavedPrimaryMark(savedMark);
      }
      invalidateRegisterContext(registerContextDocId);
    };
    const refreshButton = (
      <button
        type="button"
        onClick={refreshRegisterContext}
        disabled={registerContext.loading}
        style={{
          fontSize: 11,
          color: 'var(--raven-text-secondary)',
          background: 'none',
          border: 'none',
          padding: 0,
          cursor: registerContext.loading ? 'default' : 'pointer',
          opacity: registerContext.loading ? 0.5 : 1,
        }}
        onMouseEnter={(e) => {
          if (!registerContext.loading) e.currentTarget.style.color = 'var(--raven-text-primary)';
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.color = 'var(--raven-text-secondary)';
        }}
      >
        ↻ Refresh
      </button>
    );

    // Until the register answers, the checkbox would state a claim nobody has
    // read yet, and the canonical fields would look editable when they may not
    // be — so say which it is instead of showing the control.
    if (registerLoading || registerError) {
      return (
        <div
          style={{
            ...PRIMARY_SOURCE_PANEL_STYLE,
            display: 'flex',
            alignItems: 'flex-start',
            justifyContent: 'space-between',
            gap: 8,
          }}
        >
          <div
            style={{
              fontSize: 11,
              color: registerError ? 'var(--raven-warning-text)' : 'var(--raven-text-secondary)',
            }}
          >
            {registerError
              ? `Primary P&ID unavailable — ${registerError}`
              : 'Checking the register for this asset…'}
          </div>
          {refreshButton}
        </div>
      );
    }
    const entry = equipmentRegisterEntry;

    return (
      <div style={PRIMARY_SOURCE_PANEL_STYLE}>
        <div style={{ display: 'flex', justifyContent: 'flex-end' }}>{refreshButton}</div>
        <label style={{ display: 'flex', alignItems: 'flex-start', gap: 8, fontSize: 12 }}>
          <input
            type="checkbox"
            checked={isPrimarySourceChecked}
            onChange={(e) => setPrimarySource(e.target.checked)}
            disabled={readOnly}
            /* accent-color: without it a native checkbox paints in the OS accent
               (pink on some Macs), which read as off-brand against the green app. */
            style={{ marginTop: 2, accentColor: 'var(--raven-primary-main)' }}
          />
          <span>
            <span style={{ fontWeight: 600, color: 'var(--raven-text-secondary)' }}>Primary P&amp;ID</span>
            <div style={{ color: 'var(--raven-text-secondary)', marginTop: 2 }}>
              This drawing owns the tag, type and description of the register asset.
            </div>
          </span>
        </label>
        {entry && (
          <div style={{ fontSize: 11, color: 'var(--raven-text-secondary)', marginTop: 6 }}>
            Primary: {renderPrimaryDrawing(entry)}
          </div>
        )}
        {entry && entry.appearances.some((a) => a.doc_id && a.doc_id !== registerContextDocId) && (
          <div style={{ fontSize: 11, color: 'var(--raven-text-secondary)', marginTop: 4 }}>
            Also appears on:{' '}
            {entry.appearances
              .filter((a) => a.doc_id && a.doc_id !== registerContextDocId)
              .map((a, i) => (
                <span key={a.source_file_id}>
                  {i > 0 && ', '}
                  <a
                    href={buildEquipmentEditHref(a.doc_id as string, a.equipment_id)}
                    target="_blank"
                    rel="noreferrer"
                    style={{ color: 'var(--raven-text-secondary)' }}
                  >
                    {a.drawing_no || 'untitled drawing'}
                  </a>
                </span>
              ))}
          </div>
        )}
        {canonicalFieldsLocked && entry && (
          <div style={{ fontSize: 11, color: 'var(--raven-warning-text)', marginTop: 4 }}>
            Type, sub-type and description show the values from {describePrimaryDrawing(entry)}.
            Mark this drawing primary to edit them here.
          </div>
        )}
      </div>
    );
  };

  // Connected Equipment editor (bidirectional peer links)
  const renderConnectedEquipmentSection = () => {
    const currentId = values._markExistingId || values.id || activeEquipment?.id || '';
    const connections: Array<{ equipment_id: string; role: string }> = Array.isArray(
      values.connected_equipments
    )
      ? values.connected_equipments
      : [];

    const peerOptions: AutocompleteOption[] = equipments
      .filter((eq) => eq.id && eq.id !== currentId)
      .map((eq) => ({
        value: eq.id,
        label: eq.equipment_no || 'Unnamed equipment',
      }));

    const setConnections = (next: Array<{ equipment_id: string; role: string }>) => {
      setField('connected_equipments', next);
    };

    const updateRow = (
      idx: number,
      patch: Partial<{ equipment_id: string; role: string }>
    ) => {
      setConnections(connections.map((c, i) => (i === idx ? { ...c, ...patch } : c)));
    };

    const removeRow = (idx: number) => {
      setConnections(connections.filter((_, i) => i !== idx));
    };

    const addRow = () => {
      setConnections([...connections, { equipment_id: '', role: '' }]);
    };

    return (
      <div style={{ marginTop: 4 }}>
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: 6,
          }}
        >
          <div
            style={{
              fontSize: 10,
              fontWeight: 600,
              color: 'var(--raven-text-secondary)',
              textTransform: 'uppercase',
              letterSpacing: '0.5px',
            }}
          >
            Connected Equipment ({connections.length})
          </div>
          <button
            type="button"
            onClick={addRow}
            disabled={readOnly}
            style={addRowButtonStyle(readOnly)}
            title="Add a peer equipment connection"
          >
            + Add connection
          </button>
        </div>

        {connections.length === 0 ? (
          <div
            style={{
              padding: 10,
              textAlign: 'center',
              color: 'var(--raven-text-disabled)',
              fontSize: 11,
              backgroundColor: 'var(--raven-surface-tier2)',
              borderRadius: 6,
            }}
          >
            No connections. Bidirectionally mirrored to the peer on save.
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            {connections.map((c, idx) => (
              <div
                key={idx}
                style={{
                  display: 'grid',
                  gridTemplateColumns: '1fr 1fr auto',
                  gap: 6,
                  alignItems: 'center',
                  padding: 6,
                  background: 'var(--raven-surface-tier2)',
                  border: '1px solid var(--raven-divider)',
                  borderRadius: 6,
                }}
              >
                <AutocompleteDropdown
                  value={c.equipment_id}
                  onChange={(val) => updateRow(idx, { equipment_id: val })}
                  options={peerOptions}
                  placeholder="Select equipment..."
                  allowCustom={false}
                />
                <input
                  type="text"
                  value={c.role}
                  onChange={(e) => updateRow(idx, { role: e.target.value })}
                  placeholder="Role (e.g. feeds, drains to)"
                  style={{
                    padding: 6,
                    fontSize: 11,
                    border: '1px solid var(--raven-divider)',
                    borderRadius: 4,
                  }}
                />
                <button
                  type="button"
                  onClick={() => removeRow(idx)}
                  disabled={readOnly}
                  style={removeRowButtonStyle(readOnly)}
                  title="Remove connection"
                  aria-label="Remove connection"
                >
                  ×
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
    );
  };

  /** The compound-tag warning band, and the split editor it opens.
   *
   *  Offered only where the parent supplied a split handler — an existing P&ID
   *  equipment. The editor itself is shared with the plant hierarchy; this panel
   *  supplies the drawing's own gate on top of it.
   */
  const renderCompoundSplitSection = () => {
    if (!onSplitCompoundEquipment) return null;
    const compoundTag = String(values.equipment_no ?? '');
    if (!isCompoundTag(compoundTag)) return null;

    return (
      <CompoundSplitEditor
        docId={currentDocument?.id}
        equipmentId={editedEquipmentId}
        tag={compoundTag}
        description={String(values.description ?? '')}
        inheritedType={compoundSplitInheritedType}
        /* The split rewrites the tag, so only the drawing that owns the asset's
           canonical fields may run it. */
        blocked={readOnly || canonicalFieldsLocked}
        blockedReason={
          canonicalFieldsLocked ? 'The primary drawing owns this tag. Split it there.' : null
        }
        onConfirm={onSplitCompoundEquipment}
      />
    );
  };

  const renderEquipmentDocumentLinks = (target: 'pnid' | 'pfd') => {
    const isPnidTarget = target === 'pnid';
    const fieldKey = isPnidTarget ? 'linked_pnids' : 'linked_pfds';
    const rows: Array<LinkedPnidRef | LinkedPfdRef> = Array.isArray(values[fieldKey])
      ? values[fieldKey]
      : [];
    const targetDiagramType = isPnidTarget ? 'pid' : 'pfd';
    const currentPlant = currentDocument?.plant ?? null;
    const docOptions = allDocuments
      .filter((doc) => {
        const docType = doc.diagram_type === 'pfd' ? 'pfd' : 'pid';
        if (doc.id === currentDocument?.id) return false;
        if (docType !== targetDiagramType) return false;
        if (currentPlant && doc.plant && doc.plant !== currentPlant) return false;
        return true;
      })
      .map((doc) => ({
        value: doc.id,
        label: doc.pnid_no || doc.original_filename || doc.filename || doc.id,
      }));

    const getStoredDocumentLabel = (docId: string) => {
      const option = docOptions.find((doc) => doc.value === docId);
      if (option) return option.label;
      const doc = allDocuments.find((candidate) => candidate.id === docId);
      return doc?.pnid_no || doc?.original_filename || doc?.filename || (isPnidTarget ? 'Selected P&ID' : 'Selected PFD');
    };

    const setRows = (next: Array<LinkedPnidRef | LinkedPfdRef>) => {
      setField(fieldKey, next);
    };

    const updateRow = (idx: number, patch: Partial<LinkedPnidRef & LinkedPfdRef>) => {
      setRows(rows.map((row, i) => (i === idx ? { ...row, ...patch } : row)));
    };

    const addRow = () => {
      if (isPnidTarget) {
        setRows([
          ...rows,
          {
            pnid_file_id: '',
            equipment_id: '',
            status: 'confirmed',
            source: 'manual',
          } as LinkedPnidRef,
        ]);
      } else {
        setRows([
          ...rows,
          {
            pfd_file_id: '',
            equipment_id: '',
            status: 'confirmed',
            source: 'manual',
          } as LinkedPfdRef,
        ]);
      }
    };

    const removeRow = (idx: number) => {
      setRows(rows.filter((_, i) => i !== idx));
    };

    const onDocumentChange = (idx: number, docId: string) => {
      updateRow(idx, {
        ...(isPnidTarget ? { pnid_file_id: docId } : { pfd_file_id: docId }),
        equipment_id: '',
      });
      if (docId && onLoadLinkedDocumentState && !linkedDocumentStates[docId]) {
        void onLoadLinkedDocumentState(docId);
      }
    };

    return (
      <div style={{ marginTop: 4 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
          <div style={{ fontSize: 10, fontWeight: 600, color: 'var(--raven-text-secondary)', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
            {isPnidTarget ? 'Linked P&IDs' : 'Linked PFDs'} ({rows.length})
          </div>
          <button
            type="button"
            onClick={addRow}
            disabled={readOnly}
            style={addRowButtonStyle(readOnly)}
          >
            + Add link
          </button>
        </div>

        {rows.length === 0 ? (
          <div style={{ padding: 10, textAlign: 'center', color: 'var(--raven-text-disabled)', fontSize: 11, backgroundColor: 'var(--raven-surface-tier2)', borderRadius: 6 }}>
            No cross-document equipment links.
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            {rows.map((row, idx) => {
              const targetFileId = isPnidTarget
                ? (row as LinkedPnidRef).pnid_file_id
                : (row as LinkedPfdRef).pfd_file_id;
              const targetState = linkedDocumentStates[targetFileId];
              const equipmentOptions = (targetState?.equipments ?? []).map((eq) => ({
                value: eq.id,
                label: eq.equipment_no || eq.id,
              }));
              const storedDocLabel = targetFileId ? getStoredDocumentLabel(targetFileId) : 'No document selected';
              const docOptionsWithStored =
                targetFileId && !docOptions.some((option) => option.value === targetFileId)
                  ? [{ value: targetFileId, label: `${storedDocLabel} (current)` }, ...docOptions]
                  : docOptions;
              const storedEquipment = targetState?.equipments.find((eq) => eq.id === row.equipment_id);
              const storedEquipmentLabel = row.equipment_id
                ? storedEquipment?.equipment_no || 'Selected equipment'
                : 'No equipment selected';
              const equipmentOptionsWithStored =
                row.equipment_id && !equipmentOptions.some((option) => option.value === row.equipment_id)
                  ? [{ value: row.equipment_id, label: `${storedEquipmentLabel} (current)` }, ...equipmentOptions]
                  : equipmentOptions;
              return (
                <div
                  key={idx}
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 6,
                    padding: 6,
                    background: 'var(--raven-surface-tier2)',
                    border: '1px solid var(--raven-divider)',
                    borderRadius: 6,
                  }}
                >
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 6 }}>
                    <select
                      value={targetFileId}
                      onChange={(e) => onDocumentChange(idx, e.target.value)}
                      disabled={readOnly}
                      style={{ padding: 6, fontSize: 11, border: '1px solid var(--raven-divider)', borderRadius: 4, minWidth: 0 }}
                    >
                      <option value="">{isPnidTarget ? 'Select P&ID' : 'Select PFD'}</option>
                      {docOptionsWithStored.map((option) => (
                        <option key={option.value} value={option.value}>{option.label}</option>
                      ))}
                    </select>
                    <select
                      value={row.equipment_id}
                      onChange={(e) => updateRow(idx, { equipment_id: e.target.value })}
                      disabled={readOnly || !targetFileId}
                      style={{ padding: 6, fontSize: 11, border: '1px solid var(--raven-divider)', borderRadius: 4, minWidth: 0 }}
                    >
                      <option value="">{targetFileId && !targetState ? 'Loading...' : 'Select equipment'}</option>
                      {equipmentOptionsWithStored.map((option) => (
                        <option key={option.value} value={option.value}>{option.label}</option>
                      ))}
                    </select>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', gap: 6, alignItems: 'center' }}>
                    <select
                      value={row.status ?? 'confirmed'}
                      onChange={(e) => updateRow(idx, { status: e.target.value as 'confirmed' | 'needs_review' })}
                      disabled={readOnly}
                      style={{ padding: 6, fontSize: 11, border: '1px solid var(--raven-divider)', borderRadius: 4, minWidth: 110 }}
                    >
                      <option value="confirmed">Confirmed</option>
                      <option value="needs_review">Needs review</option>
                    </select>
                    <div style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
                      {targetFileId && row.equipment_id && onOpenLinkedDocument && (
                        <button
                          type="button"
                          onClick={() => onOpenLinkedDocument({ docId: targetFileId, targetEquipmentId: row.equipment_id, newTab: true })}
                          style={openLinkedDocumentButtonStyle}
                        >
                          Open in new tab
                        </button>
                      )}
                      <button
                        type="button"
                        onClick={() => removeRow(idx)}
                        disabled={readOnly}
                        style={removeRowButtonStyle(readOnly)}
                        title="Remove link"
                        aria-label="Remove link"
                      >
                        ×
                      </button>
                    </div>
                  </div>
                  <div
                    style={{
                      display: 'grid',
                      gridTemplateColumns: '1fr',
                      gap: 5,
                      paddingTop: 2,
                      fontSize: 11,
                      color: 'var(--raven-text-secondary)',
                      overflowWrap: 'anywhere',
                    }}
                  >
                    <div><strong>{isPnidTarget ? 'Linked P&ID' : 'Linked PFD'}:</strong> {storedDocLabel}</div>
                    <div><strong>Equipment:</strong> {storedEquipmentLabel}</div>
                    <div><strong>Status:</strong> {formatLinkStatus(row.status)}</div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    );
  };

  const renderPfdStreamDocumentLinks = () => {
    const rows: PnidLinkedPfdStreamRef[] = Array.isArray(values.linked_pfd_streams)
      ? values.linked_pfd_streams
      : [];
    const currentPlant = currentDocument?.plant ?? null;
    const docOptions = allDocuments
      .filter((doc) => {
        const docType = doc.diagram_type === 'pfd' ? 'pfd' : 'pid';
        if (doc.id === currentDocument?.id) return false;
        if (docType !== 'pfd') return false;
        if (currentPlant && doc.plant && doc.plant !== currentPlant) return false;
        return true;
      })
      .map((doc) => ({
        value: doc.id,
        label: doc.pnid_no || doc.original_filename || doc.filename || doc.id,
      }));

    const getStoredPfdLabel = (row: PnidLinkedPfdStreamRef) => {
      const option = docOptions.find((doc) => doc.value === row.pfd_file_id);
      if (option) return option.label;
      const doc = allDocuments.find((candidate) => candidate.id === row.pfd_file_id);
      return doc?.pnid_no || row.pfd_no || doc?.original_filename || doc?.filename || 'Selected PFD';
    };

    const sourceOpcSnapshot = getNodeOpcSnapshot(
      [...equipmentInstruments, ...nodeInstruments, ...directNodeInstruments],
      effectiveNode?.id,
    );

    const sourceSnapshotPatch = (): Partial<PnidLinkedPfdStreamRef> => ({
      source_line_no_snapshot: effectiveNode?.line_no ?? null,
      source_opc_instrument_id: sourceOpcSnapshot.instrumentId,
      source_opc_tag: sourceOpcSnapshot.tag,
    });

    const setRows = (next: PnidLinkedPfdStreamRef[]) => {
      setField('linked_pfd_streams', next);
    };

    const updateRow = (idx: number, patch: Partial<PnidLinkedPfdStreamRef>) => {
      setRows(rows.map((row, i) => (i === idx ? { ...row, ...patch } : row)));
    };

    const addRow = () => {
      setRows([
        ...rows,
        {
          pfd_file_id: '',
          pfd_no: null,
          node_id: '',
          source_line_no_snapshot: effectiveNode?.line_no ?? null,
          target_line_no_snapshot: null,
          source_opc_instrument_id: sourceOpcSnapshot.instrumentId,
          source_opc_tag: sourceOpcSnapshot.tag,
          target_opc_instrument_id: null,
          target_opc_tag: null,
          endpoint_role: 'dest_pnid',
          direction: 'source_to_dest',
          status: 'confirmed',
          source: 'manual',
        },
      ]);
    };

    const removeRow = (idx: number) => {
      setRows(rows.filter((_, i) => i !== idx));
    };

    const onDocumentChange = (idx: number, docId: string) => {
      const selectedDoc = allDocuments.find((doc) => doc.id === docId);
      updateRow(idx, {
        ...sourceSnapshotPatch(),
        pfd_file_id: docId,
        pfd_no: selectedDoc?.pnid_no || selectedDoc?.original_filename || selectedDoc?.filename || null,
        node_id: '',
        target_line_no_snapshot: null,
        target_opc_instrument_id: null,
        target_opc_tag: null,
      });
      if (docId && onLoadLinkedDocumentState && !linkedDocumentStates[docId]) {
        void onLoadLinkedDocumentState(docId);
      }
    };

    const onStreamChange = (idx: number, nodeId: string) => {
      const row = rows[idx];
      const targetState = row?.pfd_file_id ? linkedDocumentStates[row.pfd_file_id] : null;
      const targetNode = targetState?.nodes.find((node) => node.id === nodeId);
      const targetOpcSnapshot = getNodeOpcSnapshot(targetState?.instruments ?? [], nodeId);
      updateRow(idx, {
        ...sourceSnapshotPatch(),
        node_id: nodeId,
        target_line_no_snapshot: targetNode?.line_no ?? null,
        target_opc_instrument_id: targetOpcSnapshot.instrumentId,
        target_opc_tag: targetOpcSnapshot.tag,
      });
    };

    return (
      <div style={{ marginTop: 4 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
          <div style={{ fontSize: 10, fontWeight: 600, color: 'var(--raven-text-secondary)', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
            Linked PFD Streams ({rows.length})
          </div>
          <button
            type="button"
            onClick={addRow}
            disabled={readOnly}
            style={addRowButtonStyle(readOnly)}
          >
            + Add stream
          </button>
        </div>

        {rows.length === 0 ? (
          <div style={{ padding: 10, textAlign: 'center', color: 'var(--raven-text-disabled)', fontSize: 11, backgroundColor: 'var(--raven-surface-tier2)', borderRadius: 6 }}>
            No cross-PFD stream links.
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            {rows.map((row, idx) => {
              const targetState = linkedDocumentStates[row.pfd_file_id];
              const streamOptions = (targetState?.nodes ?? [])
                .slice()
                .sort((a, b) => a.node_no - b.node_no)
                .map((node) => ({
                  value: node.id,
                  label: node.line_no || `Stream ${node.node_no}`,
                }));
              const storedPfdLabel = row.pfd_file_id ? getStoredPfdLabel(row) : 'No PFD selected';
              const docOptionsWithStored =
                row.pfd_file_id && !docOptions.some((option) => option.value === row.pfd_file_id)
                  ? [{ value: row.pfd_file_id, label: `${storedPfdLabel} (current)` }, ...docOptions]
                  : docOptions;
              const storedStream = targetState?.nodes.find((node) => node.id === row.node_id);
              const storedStreamLabel = row.node_id
                ? storedStream?.line_no || row.target_line_no_snapshot || (storedStream ? `Stream ${storedStream.node_no}` : 'Selected stream')
                : 'No stream selected';
              const targetOpcSnapshot = getNodeOpcSnapshot(targetState?.instruments ?? [], row.node_id);
              const displaySourceOpcTag = row.source_opc_tag || sourceOpcSnapshot.tag;
              const displayTargetOpcTag = row.target_opc_tag || targetOpcSnapshot.tag;
              const streamOptionsWithStored =
                row.node_id && !streamOptions.some((option) => option.value === row.node_id)
                  ? [{ value: row.node_id, label: `${storedStreamLabel} (current)` }, ...streamOptions]
                  : streamOptions;
              return (
                <div
                  key={idx}
                  style={{
                    padding: 6,
                    background: 'var(--raven-surface-tier2)',
                    border: '1px solid var(--raven-divider)',
                    borderRadius: 6,
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 6,
                  }}
                >
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 6 }}>
                    <select
                      value={row.pfd_file_id}
                      onChange={(e) => onDocumentChange(idx, e.target.value)}
                      disabled={readOnly}
                      style={{ padding: 6, fontSize: 11, border: '1px solid var(--raven-divider)', borderRadius: 4, minWidth: 0 }}
                    >
                      <option value="">Select PFD</option>
                      {docOptionsWithStored.map((option) => (
                        <option key={option.value} value={option.value}>{option.label}</option>
                      ))}
                    </select>
                    <select
                      value={row.node_id}
                      onChange={(e) => onStreamChange(idx, e.target.value)}
                      disabled={readOnly || !row.pfd_file_id}
                      style={{ padding: 6, fontSize: 11, border: '1px solid var(--raven-divider)', borderRadius: 4, minWidth: 0 }}
                    >
                      <option value="">{row.pfd_file_id && !targetState ? 'Loading...' : 'Select stream'}</option>
                      {streamOptionsWithStored.map((option) => (
                        <option key={option.value} value={option.value}>{option.label}</option>
                      ))}
                    </select>
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 6 }}>
                    <select
                      value={row.endpoint_role}
                      onChange={(e) => updateRow(idx, { endpoint_role: e.target.value as 'source_pnid' | 'dest_pnid' })}
                      disabled={readOnly}
                      style={{ padding: 6, fontSize: 11, border: '1px solid var(--raven-divider)', borderRadius: 4 }}
                    >
                      <option value="source_pnid">Source P&ID</option>
                      <option value="dest_pnid">Destination P&ID</option>
                    </select>
                    <select
                      value={row.direction}
                      onChange={(e) => updateRow(idx, { direction: e.target.value as 'source_to_dest' | 'dest_to_source' })}
                      disabled={readOnly}
                      style={{ padding: 6, fontSize: 11, border: '1px solid var(--raven-divider)', borderRadius: 4 }}
                    >
                      <option value="source_to_dest">Source to destination</option>
                      <option value="dest_to_source">Destination to source</option>
                    </select>
                    <select
                      value={row.status ?? 'confirmed'}
                      onChange={(e) => updateRow(idx, { status: e.target.value as 'confirmed' | 'needs_review' })}
                      disabled={readOnly}
                      style={{ padding: 6, fontSize: 11, border: '1px solid var(--raven-divider)', borderRadius: 4 }}
                    >
                      <option value="confirmed">Confirmed</option>
                      <option value="needs_review">Needs review</option>
                    </select>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', gap: 6, alignItems: 'center' }}>
                    <div style={{ fontSize: 10, color: 'var(--raven-text-secondary)' }}>{formatLinkStatus(row.status)}</div>
                    <div style={{ display: 'flex', gap: 4 }}>
                      {row.pfd_file_id && row.node_id && onOpenLinkedDocument && (
                        <button
                          type="button"
                          onClick={() => onOpenLinkedDocument({ docId: row.pfd_file_id, targetNodeId: row.node_id, newTab: true })}
                          style={openLinkedDocumentButtonStyle}
                        >
                          Open in new tab
                        </button>
                      )}
                      <button
                        type="button"
                        onClick={() => removeRow(idx)}
                        disabled={readOnly}
                        style={{ padding: '4px 8px', fontSize: 10, color: 'var(--raven-text-secondary)', background: 'white', border: '1px solid var(--raven-divider)', borderRadius: 3, cursor: readOnly ? 'not-allowed' : 'pointer', opacity: readOnly ? 0.55 : 1 }}
                        title="Remove stream link"
                        aria-label="Remove stream link"
                      >
                        x
                      </button>
                    </div>
                  </div>
                  <div
                    style={{
                      display: 'grid',
                      gridTemplateColumns: '1fr',
                      gap: 5,
                      fontSize: 11,
                      color: 'var(--raven-text-secondary)',
                      overflowWrap: 'anywhere',
                    }}
                  >
                    <div><strong>Linked PFD:</strong> {storedPfdLabel}</div>
                    <div><strong>Stream:</strong> {storedStreamLabel}</div>
                    <div><strong>Source OPC:</strong> {formatStoredText(displaySourceOpcTag)}</div>
                    <div><strong>Target OPC:</strong> {formatStoredText(displayTargetOpcTag)}</div>
                    <div><strong>Status:</strong> {formatLinkStatus(row.status)}</div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    );
  };

  // Render line_spec fields for node
  const renderLineSpecFields = () => {
    const lineSpec = values.line_spec || {};
    if (Object.keys(lineSpec).length === 0) return null;

    return (
      <div style={{ marginTop: 8 }}>
        <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--raven-text-secondary)', marginBottom: 6 }}>
          Line Specification (from AI)
        </div>
        <div style={{ 
          padding: 8, 
          background: 'var(--raven-surface-tier3)', 
          border: '1px solid var(--raven-primary-light)', 
          borderRadius: 6, 
          fontSize: 11,
          display: 'flex',
          flexWrap: 'wrap',
          gap: 8
        }}>
          {lineSpec.size_in && (
            <span style={{ padding: '2px 6px', background: 'var(--raven-surface-tier3)', borderRadius: 4 }}>
              Size: {lineSpec.size_in}"
            </span>
          )}
          {lineSpec.service_code && (
            <span style={{ padding: '2px 6px', background: 'var(--raven-surface-tier3)', borderRadius: 4 }}>
              Service: {lineSpec.service_code}
            </span>
          )}
          {lineSpec.pipe_class && (
            <span style={{ padding: '2px 6px', background: 'var(--raven-surface-tier3)', borderRadius: 4 }}>
              Class: {lineSpec.pipe_class}
            </span>
          )}
          {lineSpec.sequence_no && (
            <span style={{ padding: '2px 6px', background: 'var(--raven-surface-tier3)', borderRadius: 4 }}>
              Seq: {lineSpec.sequence_no}
            </span>
          )}
          {lineSpec.remark && (
            <span style={{ padding: '2px 6px', background: 'var(--raven-warning-bg)', borderRadius: 4 }}>
              ({lineSpec.remark})
            </span>
          )}
        </div>
      </div>
    );
  };

  // Equipment instrument list (for kind === 'equipment')
  const activeEquipmentInstruments = useMemo(() => {
    if (kind !== 'equipment' || !activeEquipment) return [];
    const instrumentState = { instruments: [...equipmentInstruments, ...nodeInstruments] };
    return equipmentInstruments.filter(i => getInstrumentEffectiveContext(i, instrumentState).context_id === activeEquipment.id);
  }, [kind, activeEquipment, equipmentInstruments, nodeInstruments]);

  // Instrument list drag handlers (for kind === 'node')
  const activeNodeInstruments = useMemo(() => {
    if (kind !== 'node' || !effectiveNode) return [];
    const instrumentState = { instruments: [...equipmentInstruments, ...nodeInstruments] };
    return nodeInstruments.filter(i => getInstrumentEffectiveContext(i, instrumentState).context_id === effectiveNode.id);
  }, [kind, effectiveNode, equipmentInstruments, nodeInstruments]);

  const activeDirectNodeInstruments = useMemo(() => {
    if (kind !== 'node' || !effectiveNode) return [];
    return directNodeInstruments.filter(i => i.context_id === effectiveNode.id);
  }, [kind, effectiveNode, directNodeInstruments]);

  const handleInstDragStart = (index: number) => setDraggedIndex(index);
  const handleInstDragOver = (e: React.DragEvent, index: number) => {
    e.preventDefault();
    if (draggedIndex === null) return;
    setDragOverIndex(index);
  };
  const handleInstDrop = (index: number) => {
    if (readOnly) {
      setDraggedIndex(null);
      setDragOverIndex(null);
      return;
    }
    if (draggedIndex === null || !onReorderInstruments) return;
    if (draggedIndex !== index) {
      const dragged = sortedNodeInstruments[draggedIndex];
      const directIds = new Set(activeDirectNodeInstruments.map((inst) => inst.id));
      if (dragged && directIds.has(dragged.id)) {
        const directItems = sortedNodeInstruments.filter((inst) => directIds.has(inst.id));
        const from = directItems.findIndex((inst) => inst.id === dragged.id);
        const to = sortedNodeInstruments.slice(0, index).filter((inst) => directIds.has(inst.id)).length;
        if (from !== -1) {
          const nextDirect = [...directItems];
          const [removed] = nextDirect.splice(from, 1);
          nextDirect.splice(Math.max(0, Math.min(nextDirect.length, to)), 0, removed);
          onReorderInstruments(nextDirect);
        }
      }
    }
    setDraggedIndex(null);
    setDragOverIndex(null);
  };
  const handleInstDragEnd = () => {
    setDraggedIndex(null);
    setDragOverIndex(null);
  };

  const sortedNodeInstruments = useMemo(() => {
    return [...activeNodeInstruments].sort((a, b) => {
      const seqA = a.sequence_order ?? Infinity;
      const seqB = b.sequence_order ?? Infinity;
      return seqA - seqB;
    });
  }, [activeNodeInstruments]);

  const activeDirectNodeInstrumentIds = useMemo(
    () => new Set(activeDirectNodeInstruments.map((inst) => inst.id)),
    [activeDirectNodeInstruments],
  );

  const reviewStatusColor = (
    status?: string | null,
    kind: 'equipment' | 'node' | 'instrument' = 'instrument'
  ) => {
    if (status === 'final_and_reviewed') return 'var(--raven-success)';
    if (status === 'final' || status === 'in_progress') {
      if (kind === 'equipment') return 'var(--raven-info)';
      if (kind === 'node') return 'var(--raven-warning)';
      return 'var(--raven-primary-main)';
    }
    if (status === 'skipped') return 'var(--raven-primary-main)';
    return 'var(--raven-text-disabled)';
  };

  const renderPfdAttachedInstruments = () => {
    if (kind === 'equipment') {
      if (!activeEquipment) return null;
      return (
        <div style={{ marginTop: 4 }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 6 }}>
            <div style={{ fontSize: 10, fontWeight: 600, color: 'var(--raven-text-secondary)', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
              Instruments ({activeEquipmentInstruments.length})
            </div>
            {onMarkInstrument && (
              <button
                type="button"
                onClick={() => onMarkInstrument('equipment', activeEquipment.id)}
                disabled={readOnly}
                style={{
                  padding: '2px 8px',
                  fontSize: 9,
                  fontWeight: 500,
                  color: 'var(--raven-primary-main)',
                  backgroundColor: 'var(--raven-state-selected)',
                  border: '1px solid var(--raven-primary-light)',
                  borderRadius: 4,
                  cursor: readOnly ? 'not-allowed' : 'pointer',
                  opacity: readOnly ? 0.55 : 1,
                }}
              >
                + Mark Instrument
              </button>
            )}
          </div>
          {activeEquipmentInstruments.length === 0 ? (
            <div style={{ padding: 12, textAlign: 'center', color: 'var(--raven-text-disabled)', fontSize: 12, backgroundColor: 'var(--raven-surface-tier2)', borderRadius: 6 }}>
              No instruments attached yet.
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
              {activeEquipmentInstruments.map((inst) => (
                <div
                  key={inst.id}
                  onMouseEnter={() => onHighlightEntity?.(inst.id)}
                  onMouseLeave={() => onHighlightEntity?.(null)}
                  style={{ padding: '8px 10px', backgroundColor: 'var(--raven-surface-tier2)', border: '1px solid var(--raven-divider)', borderRadius: 6, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}
                >
                  <span style={{ width: 6, height: 6, borderRadius: 99, backgroundColor: reviewStatusColor(inst.status, 'instrument'), flexShrink: 0 }} />
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--raven-text-primary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {getInstrumentPrimaryLabel(inst)}
                    </div>
                    <div style={{ fontSize: 10, color: 'var(--raven-text-secondary)', marginTop: 1 }}>
                      {inst.instrument_type || 'Instrument'}
                    </div>
                  </div>
                  <div style={{ display: 'flex', gap: 3, flexShrink: 0 }}>
                    <button type="button" onClick={() => onEditEquipmentInstrument?.(inst.id)} disabled={readOnly} style={rowEditButtonStyle(readOnly)}>
                      Edit
                    </button>
                    <button type="button" onClick={() => onUnattachEquipmentInstrument?.(inst.id)} disabled={readOnly} style={{ padding: '3px 6px', fontSize: 10, color: 'var(--raven-text-disabled)', backgroundColor: 'transparent', border: '1px solid var(--raven-divider)', borderRadius: 3, cursor: readOnly ? 'not-allowed' : 'pointer', opacity: readOnly ? 0.55 : 1 }} title="Unattach from equipment">
                      {'\u2715'}
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      );
    }

    if (!effectiveNode) return null;
    return (
      <div id={findingAnchorId(findingFormId, 'instruments')} style={{ marginTop: 4 }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 6 }}>
          <div style={{ fontSize: 10, fontWeight: 600, color: 'var(--raven-text-secondary)', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
            Instruments ({sortedNodeInstruments.length})
          </div>
          {onMarkInstrument && (
            <button
              type="button"
              onClick={() => onMarkInstrument('node', effectiveNode.id)}
              disabled={readOnly}
              style={{ padding: '2px 8px', fontSize: 9, fontWeight: 500, color: 'var(--raven-primary-main)', backgroundColor: 'var(--raven-state-selected)', border: '1px solid var(--raven-primary-light)', borderRadius: 4, cursor: readOnly ? 'not-allowed' : 'pointer', opacity: readOnly ? 0.55 : 1 }}
            >
              + Mark Instrument
            </button>
          )}
        </div>
        {sortedNodeInstruments.length === 0 ? (
          <div style={{ padding: 12, textAlign: 'center', color: 'var(--raven-text-disabled)', fontSize: 12, backgroundColor: 'var(--raven-surface-tier2)', borderRadius: 6 }}>
            No instruments marked yet.
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            {sortedNodeInstruments.map((inst, index) => (
              <div
                key={inst.id}
                onMouseEnter={() => onHighlightEntity?.(inst.id)}
                onMouseLeave={() => onHighlightEntity?.(null)}
                style={{ padding: '8px 10px', backgroundColor: 'var(--raven-surface-tier2)', border: '1px solid var(--raven-divider)', borderRadius: 6, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}
              >
                <span style={{ width: 16, height: 16, display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: 'var(--raven-surface-tier2)', color: 'var(--raven-text-secondary)', borderRadius: 3, fontSize: 9, fontWeight: 600 }}>
                  {inst.sequence_order ?? index + 1}
                </span>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--raven-text-primary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {getInstrumentPrimaryLabel(inst)}
                  </div>
                  <div style={{ fontSize: 10, color: 'var(--raven-text-secondary)', marginTop: 1 }}>
                    {inst.instrument_type || 'Instrument'}
                  </div>
                </div>
                <div style={{ display: 'flex', gap: 3, flexShrink: 0 }}>
                  <button type="button" onClick={() => onEditInstrument?.(inst.id)} disabled={readOnly} style={rowEditButtonStyle(readOnly)}>
                    Edit
                  </button>
                  <button type="button" onClick={() => onUnattachInstrument?.(inst.id)} disabled={readOnly} style={{ padding: '3px 6px', fontSize: 10, color: 'var(--raven-text-disabled)', backgroundColor: 'transparent', border: '1px solid var(--raven-divider)', borderRadius: 3, cursor: readOnly ? 'not-allowed' : 'pointer', opacity: readOnly ? 0.55 : 1 }} title="Unattach from node">
                    {'\u2715'}
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
        {renderFieldFindings('instruments')}
      </div>
    );
  };

  // Wire the stable PFD custom-renderer refs to the latest closures. SchemaFieldRenderer
  // hands a custom renderer the whole field, label included, so each prints its own.
  const renderPfdLineEnd = (
    { field, value, readOnly: ro }: CustomRendererProps,
    end: LineEnd,
    onPick: (selection: RefSelection) => void,
  ) => (
    <div>
      <div style={{ fontSize: 12, fontWeight: 500, marginBottom: 4 }}>{field.label}</div>
      <RefCombobox
        label={field.label}
        value={typeof value === 'string' ? value : ''}
        groups={endGroups(end)}
        onChange={onPick}
        onDraftChange={draftChange(end)}
        autoFocus={initialLineFocus === end}
        onHighlightEntity={onHighlightEntity}
        disabled={ro}
      />
      {renderEndSheetField(end, ro)}
    </div>
  );
  pfdSourceRefRenderRef.current = (p) => renderPfdLineEnd(p, 'source', pickSourceRef);
  pfdDestRefRenderRef.current = (p) => renderPfdLineEnd(p, 'destination', pickDestRef);
  pfdConnectedEqRenderRef.current = renderConnectedEquipmentSection;
  pfdLinkedPnidRenderRef.current = () => renderEquipmentDocumentLinks('pnid');
  pfdLinkedStreamRenderRef.current = renderPfdStreamDocumentLinks;

  // A draft's refusal is a line geometry conflict, so it reads under the drawn path; a form with
  // no path row shows it where Save's refusal shows.
  const renderDrawnPathRow = () => drawnPath && (
    <div style={{ fontSize: 11, color: 'var(--raven-text-secondary)' }}>
      {drawnPath.pointCount > 0 ? `${drawnPath.pointCount} points · ${drawnPath.boundCount} bound` : 'Duct box'}
      {draftRefusal && <p role="alert" className="pnid-draft-refusal">{draftRefusal}</p>}
    </div>
  );
  const formLevelError = saveError ?? (drawnPath ? null : draftRefusal);

  /**
   * DECISION(2026-10-27, Abhishek): Escape closes an equipment or line form the SME has not edited at once, and asks "Discard unsaved changes?" first when they have.
   * An untouched form holds nothing to lose, so a confirm there only slows the SME who drew a box or a line by mistake.
   */
  const closeUntouchedFormOnEscape = (event: React.KeyboardEvent) => {
    if (event.key !== 'Escape' || event.defaultPrevented) return;
    if (formEdited) return;
    event.preventDefault();
    onCancel();
  };

  const renderPfdFormFields = () => (
    <>
      {kind === 'equipment' && isMarkingExisting && values.equipment_no && (
        <div style={{
          padding: '8px 12px',
          background: 'var(--raven-surface-tier3)',
          border: '1px solid var(--raven-primary-light)',
          borderRadius: 6,
          display: 'flex',
          alignItems: 'center',
          gap: 8,
        }}>
          <span style={{ color: 'var(--raven-primary-main)', fontWeight: 600 }}>{values.equipment_no}</span>
          <span style={{ color: 'var(--raven-success)', fontSize: 11 }}>{values.equipment_type || 'Equipment'}</span>
        </div>
      )}
      {kind === 'node' && (
        <div style={{
          fontSize: 12,
          padding: '8px 12px',
          background: 'var(--raven-surface-tier3)',
          border: '1px solid var(--raven-primary-light)',
          borderRadius: 6,
          display: 'flex',
          alignItems: 'center',
          gap: 8
        }}>
          <span style={{ color: 'var(--raven-primary-main)', fontWeight: 600 }}>Stream {values.line_no || values.node_no || '?'}</span>
        </div>
      )}
      {pfdSchema ? (
        <SchemaFieldRenderer
          sections={pfdSchema.sections}
          values={values}
          setField={setField}
          customRenderers={kind === 'equipment' ? pfdEquipmentCustomRenderers : pfdNodeCustomRenderers}
          readOnly={readOnly}
        />
      ) : (
        <div style={{ padding: 10, color: 'var(--raven-text-secondary)', fontSize: 12 }}>Loading PFD form...</div>
      )}
      {kind === 'node' && renderDrawnPathRow()}
      {renderPfdAttachedInstruments()}
    </>
  );


  // Always render inline, inside the workspace's editor dock (no modal overlay)
  return (
    <div data-zone="form" onKeyDown={closeUntouchedFormOnEscape}>
      {/* The dock that wraps this form already renders a header + close
          control, so the form's own title row carries no second ✕. The dock
          title names a line form, so only the equipment form repeats a title. */}
      {kind === 'equipment' && (
        <h3 style={{ margin: 0, fontSize: 16, color: 'var(--raven-text-primary)' }}>
          {isMarkingExisting ? 'Update' : 'Mark'} Equipment
        </h3>
      )}

      {isMarkingExisting && kind === 'equipment' && (
        <div style={{ 
          marginTop: 8, 
          padding: '6px 10px', 
          background: 'var(--raven-surface-tier2)', 
          border: '1px solid var(--raven-divider)', 
          borderRadius: 6, 
          fontSize: 11, 
          color: 'var(--raven-text-primary)' 
        }}>
          Marking existing item from AI data
        </div>
      )}

      <form
        id={formId}
        onSubmit={handleSubmit}
        onBlur={draftCommitHandlers.onBlur}
        onInputCapture={draftCommitHandlers.onInputCapture}
        onPointerDownCapture={draftCommitHandlers.onPointerDownCapture}
        onKeyDownCapture={draftCommitHandlers.onKeyDownCapture}
        style={{ marginTop: 12, display: 'flex', flexDirection: 'column', gap: 10 }}
      >
        {isPfdMode && renderPfdFormFields()}

        {!isPfdMode && kind === 'equipment' && (
          <>
            {/* Equipment identity header (like node's green Node # box) */}
            {isMarkingExisting && values.equipment_no && (
              <div style={{
                padding: '8px 12px',
                background: 'var(--raven-surface-tier3)',
                border: '1px solid var(--raven-primary-light)',
                borderRadius: 6,
                display: 'flex',
                alignItems: 'center',
                gap: 8,
              }}>
                <span style={{ color: 'var(--raven-primary-main)', fontWeight: 600 }}>{values.equipment_no}</span>
                <span style={{ color: 'var(--raven-success)', fontSize: 11 }}>
                  {values.equipment_type || 'Equipment'}
                </span>
              </div>
            )}
            <div id={findingAnchorId(findingFormId, 'equipment_no')}>
              <label style={{ fontSize: 12 }}>
                Equipment No (required)
                {/* The tag stays editable on every drawing: a mistagged element is
                    re-pointed at the right asset by renaming it, primary or not. */}
                <TagAutocompleteInput
                  value={values.equipment_no ?? ''}
                  onChange={(next) => setField('equipment_no', next)}
                  onSettle={handleEquipmentTagSettled}
                  docId={currentDocument?.id}
                  kind="equipment"
                  disabled={readOnly}
                  required
                  hyphenateSpacedTag
                  style={{ marginTop: 4, fontWeight: 500, background: readOnly ? 'var(--raven-surface-tier2)' : 'white' }}
                />
              </label>
              {renderFieldFindings('equipment_no')}
            </div>

            <div>
              {/* The label points at the select rather than wrapping it, so the
                  picker's own status line stays out of the field's name. */}
              <label htmlFor={OWNER_DOMAIN_SELECT_ID} style={{ display: 'block', fontSize: 12 }}>
                Domain
              </label>
              <div style={{ fontSize: 11, color: 'var(--raven-text-secondary)', marginTop: 2 }}>
                The plant that owns this asset.
              </div>
              <ChangedField change={agentChangeFor('owner_domain_id')} onRestore={() => restoreField('owner_domain_id')}>
              <DomainSelect
                id={OWNER_DOMAIN_SELECT_ID}
                storedValue={storedOwnerDomainId}
                onChange={(next) => setField('owner_domain_id', next)}
                drawingDomainId={drawingDomainId}
                drawingPlantName={currentDocument?.plant ?? null}
                disabled={readOnly}
                style={{
                  width: '100%',
                  padding: 8,
                  marginTop: 4,
                  border: '1px solid var(--raven-divider)',
                  borderRadius: 6,
                  background: readOnly ? 'var(--raven-surface-tier2)' : 'var(--raven-surface-tier1)',
                }}
              />
              </ChangedField>
            </div>

            {/* The mark governs the fields below it, so it is answered first. */}
            {renderPrimarySourceBlock()}

            {renderCompoundSplitSection()}

            {/* Equipment Type + Description in a grouped section */}
            <div style={{ padding: 10, background: 'var(--raven-surface-tier2)', border: '1px solid var(--raven-divider)', borderRadius: 6 }}>
              <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--raven-text-secondary)', marginBottom: 8 }}>Details</div>
              <label style={{ fontSize: 12, display: 'block', marginBottom: 8 }}>
                Equipment Type (required)
                {equipmentTypes.error ? (
                  /* The taxonomy is unreachable. The API skips its check in
                     exactly this case, so keep the field usable rather than
                     trapping the reviewer behind a disabled control. */
                  <ChangedField change={agentChangeFor('equipment_type')} onRestore={() => changeEquipmentType(String(agentChangeFor('equipment_type')?.previous ?? ''))}>
                    <input
                    value={canonicalFieldsLocked
                      ? lockedEquipmentType
                      : (values.equipment_type ?? '')}
                    onChange={(e) => changeEquipmentType(e.target.value)}
                    disabled={assetFieldsLocked}
                    style={{ width: '100%', padding: 8, marginTop: 4, border: '1px solid var(--raven-divider)', borderRadius: 6, background: assetFieldsLocked ? 'var(--raven-surface-tier2)' : 'white' }}
                    required
                  />
                  </ChangedField>
                ) : (
                  <ChangedField change={agentChangeFor('equipment_type')} onRestore={() => changeEquipmentType(String(agentChangeFor('equipment_type')?.previous ?? ''))}>
                    <select
                    /* A stored value outside the reviewed list shows as no
                       selection, so `required` makes the reviewer classify it
                       before the mark can be saved. */
                    value={canonicalFieldsLocked
                      ? lockedEquipmentType
                      : (isUnreviewedEquipmentType ? '' : (values.equipment_type ?? ''))}
                    onChange={(e) => changeEquipmentType(e.target.value)}
                    disabled={equipmentTypes.loading || assetFieldsLocked}
                    style={{ width: '100%', padding: 8, marginTop: 4, border: '1px solid var(--raven-divider)', borderRadius: 6, background: assetFieldsLocked ? 'var(--raven-surface-tier2)' : 'var(--raven-surface-tier1)' }}
                    required
                  >
                    <option value="" disabled>
                      {equipmentTypes.loading ? 'Loading…' : 'Select an equipment type'}
                    </option>
                    {/* The register's type need not be in the reviewed list, and a
                        locked field must still show what the register holds. */}
                    {lockedTypeIsOffList && (
                      <option value={lockedEquipmentType}>{lockedEquipmentType}</option>
                    )}
                    {equipmentTypes.types.map((item) => (
                      <option key={item.key} value={item.key}>{item.label}</option>
                    ))}
                  </select>
                  </ChangedField>
                )}
                {isUnreviewedEquipmentType && !equipmentTypes.error && !assetFieldsLocked && (
                  <div style={{ fontSize: 11, color: 'var(--raven-warning-text)', marginTop: 4 }}>
                    Recorded as “{values.equipment_type}”, which is not in the reviewed
                    list. Choose the reviewed type to save this equipment.
                  </div>
                )}
                {equipmentTypes.error && (
                  <div style={{ fontSize: 11, color: 'var(--raven-error-text)', marginTop: 4 }}>
                    {equipmentTypes.error}. Type the equipment type instead.
                  </div>
                )}
              </label>
              {equipmentSubtypeOptions.length > 0 && (
                <label style={{ fontSize: 12, display: 'block', marginBottom: 8 }}>
                  Sub-type
                  <ChangedField change={agentChangeFor('subtype')} onRestore={() => restoreField('subtype')}>
                    <select
                    value={values.subtype ?? ''}
                    onChange={(e) => setField('subtype', e.target.value || null)}
                    disabled={assetFieldsLocked}
                    style={{ width: '100%', padding: 8, marginTop: 4, border: '1px solid var(--raven-divider)', borderRadius: 6, background: assetFieldsLocked ? 'var(--raven-surface-tier2)' : 'var(--raven-surface-tier1)' }}
                  >
                    <option value="">-- None --</option>
                    {/* A stored label the vocabulary no longer lists still has to read back. */}
                    {values.subtype && !equipmentSubtypeOptions.includes(values.subtype) && (
                      <option value={values.subtype}>{values.subtype}</option>
                    )}
                    {equipmentSubtypeOptions.map((subtype) => (
                      <option key={subtype} value={subtype}>{subtype}</option>
                    ))}
                  </select>
                  </ChangedField>
                </label>
              )}
              <label style={{ fontSize: 12, display: 'block' }}>
                Description (required)
                <ChangedField change={agentChangeFor('description')} onRestore={() => restoreField('description')}>
                  <input
                  value={canonicalFieldsLocked
                    ? equipmentRegisterEntry?.display_name ?? ''
                    : (values.description ?? '')}
                  onChange={(e) => setField('description', e.target.value)}
                  disabled={assetFieldsLocked}
                  style={{ width: '100%', padding: 8, marginTop: 4, border: '1px solid var(--raven-divider)', borderRadius: 6, background: assetFieldsLocked ? 'var(--raven-surface-tier2)' : 'white' }}
                  required
                />
                </ChangedField>
              </label>
              {assetFieldsLocked && !canonicalFieldsLocked && (
                <div style={{ fontSize: 11, color: 'var(--raven-warning-text)', marginTop: 6 }}>
                  Tick Primary P&amp;ID to edit the type, sub-type and description.
                </div>
              )}
            </div>

            {renderDetailsFields()}
            <label style={{ fontSize: 12 }}>
              Painting specification (optional)
              <ChangedField change={agentChangeFor('painting_spec')} onRestore={() => restoreField('painting_spec')}>
                <input
                value={values.painting_spec ?? ''}
                onChange={(e) => setField('painting_spec', e.target.value)}
                placeholder="Painting or coating specification..."
                disabled={readOnly}
                style={{ width: '100%', padding: 8, marginTop: 4, border: '1px solid var(--raven-divider)', borderRadius: 6, fontSize: 12 }}
              />
              </ChangedField>
            </label>

            {savedEquipmentTag && drawingDomainId && (
              <DocumentsSection domainId={drawingDomainId} tag={savedEquipmentTag} />
            )}

            {/* Connected Equipment (bidirectional peer links) */}
            {renderConnectedEquipmentSection()}

            {/* A drawing with no domain belongs to no plant, so it has no sets to show. */}
            {currentDocument?.id && currentDocument.domain_id && (
              <RedundancySetSection
                docId={currentDocument.id}
                domainId={currentDocument.domain_id}
                savedTag={activeEquipment?.equipment_no ?? ''}
                kind="equipment"
                readOnly={readOnly}
              />
            )}

            {/* Linked PFDs (bidirectional cross-document links) */}
            {renderEquipmentDocumentLinks('pfd')}

            {/* Notes field */}
            <label style={{ fontSize: 12 }}>
              Notes (optional) — attached to equipment {values.equipment_no || 'this equipment'}
              <ChangedField change={agentChangeFor('notes')} onRestore={() => restoreField('notes')}>
                <textarea
                value={values.notes ?? ''}
                onChange={(e) => setField('notes', e.target.value)}
                placeholder="Additional notes or metadata..."
                rows={2}
                disabled={readOnly}
                style={{ width: '100%', padding: 8, marginTop: 4, border: '1px solid var(--raven-divider)', borderRadius: 6, resize: 'vertical', fontSize: 12 }}
              />
              </ChangedField>
            </label>

            {/* Attached Instruments */}
            {isMarkingExisting && (
              <div style={{ marginTop: 4 }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 6 }}>
                  <div style={{ fontSize: 10, fontWeight: 600, color: 'var(--raven-text-secondary)', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                    Instruments ({activeEquipmentInstruments.length})
                  </div>
                  {onMarkInstrument && activeEquipment && (
                    <button
                      type="button"
                      onClick={() => onMarkInstrument('equipment', activeEquipment.id)}
                      disabled={readOnly}
                      style={{
                        padding: '2px 8px',
                        fontSize: 9,
                        fontWeight: 500,
                        color: 'var(--raven-primary-main)',
                        backgroundColor: 'var(--raven-state-selected)',
                        border: '1px solid var(--raven-primary-light)',
                        borderRadius: 4,
                        cursor: readOnly ? 'not-allowed' : 'pointer',
                        opacity: readOnly ? 0.55 : 1,
                        display: 'flex',
                        alignItems: 'center',
                        gap: 3,
                      }}
                      title="Draw an instrument bounding box on the canvas"
                    >
                      + Mark Instrument
                    </button>
                  )}
                </div>

                {activeEquipmentInstruments.length === 0 ? (
                  <div style={{
                    padding: 12,
                    textAlign: 'center',
                    color: 'var(--raven-text-disabled)',
                    fontSize: 12,
                    backgroundColor: 'var(--raven-surface-tier2)',
                    borderRadius: 6,
                  }}>
                    No instruments attached yet.
                  </div>
                ) : (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                    {activeEquipmentInstruments.map((inst) => {
                      const isUnattachConfirm = confirmUnattachEquip === inst.id;
                      const hasBbox = Boolean(inst.bbox);

                      return (
                        <div
                          key={inst.id}
                          onMouseEnter={() => onHighlightEntity?.(inst.id)}
                          onMouseLeave={() => onHighlightEntity?.(null)}
                          style={{
                            padding: '8px 10px',
                            backgroundColor: 'var(--raven-surface-tier2)',
                            border: '1px solid var(--raven-divider)',
                            borderRadius: 6,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            gap: 8,
                          }}
                        >
                          {/* Status indicator */}
                          <span
                            style={{
                              width: 6,
                              height: 6,
                              borderRadius: 99,
                              backgroundColor: reviewStatusColor(inst.status, 'instrument'),
                              flexShrink: 0,
                            }}
                            title={inst.status === 'final_and_reviewed' ? 'Reviewed' : hasBbox ? 'Marked' : 'Not marked'}
                          />

                            <div style={{ flex: 1, minWidth: 0 }}>
                              <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--raven-text-primary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                              {getInstrumentPrimaryLabel(inst)}
                              </div>
                              <div style={{ fontSize: 10, color: 'var(--raven-text-secondary)', marginTop: 1 }}>
                                {getEquipmentInstrumentName(inst.instrument_type)}
                              {inst.subtype ? ` \u00b7 ${inst.subtype}` : ''}
                            </div>
                          </div>

                          {/* Actions: Edit + Unattach */}
                          <div style={{ display: 'flex', gap: 3, flexShrink: 0 }}>
                            {isUnattachConfirm ? (
                              <>
                                <button
                                  type="button"
                                  onClick={() => {
                                    onUnattachEquipmentInstrument?.(inst.id);
                                    setConfirmUnattachEquip(null);
                                  }}
                                  disabled={readOnly}
                                  style={{
                                    padding: '3px 8px',
                                    fontSize: 10,
                                    fontWeight: 500,
                                    color: 'white',
                                    backgroundColor: 'var(--raven-error)',
                                    border: 'none',
                                    borderRadius: 3,
                                    cursor: readOnly ? 'not-allowed' : 'pointer',
                                    opacity: readOnly ? 0.55 : 1,
                                  }}
                                >
                                  Confirm
                                </button>
                                <button
                                  type="button"
                                  onClick={() => setConfirmUnattachEquip(null)}
                                  style={{
                                    padding: '3px 8px',
                                    fontSize: 10,
                                    fontWeight: 500,
                                    color: 'var(--raven-text-secondary)',
                                    backgroundColor: 'white',
                                    border: '1px solid var(--raven-divider)',
                                    borderRadius: 3,
                                    cursor: 'pointer',
                                  }}
                                >
                                  {'\u2715'}
                                </button>
                              </>
                            ) : (
                              <>
                                <button
                                  type="button"
                                  onClick={() => onEditEquipmentInstrument?.(inst.id)}
                                  disabled={readOnly}
                                  style={rowEditButtonStyle(readOnly)}
                                >
                                  Edit
                                </button>
                                <button
                                  type="button"
                                  onClick={() => setConfirmUnattachEquip(inst.id)}
                                  disabled={readOnly}
                                  style={{
                                    padding: '3px 6px',
                                    fontSize: 10,
                                    color: 'var(--raven-text-disabled)',
                                    backgroundColor: 'transparent',
                                    border: '1px solid var(--raven-divider)',
                                    borderRadius: 3,
                                    cursor: readOnly ? 'not-allowed' : 'pointer',
                                    opacity: readOnly ? 0.55 : 1,
                                  }}
                                  title="Unattach from equipment"
                                >
                                  {'\u2715'}
                                </button>
                              </>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            )}
          </>
        )}

        {!isPfdMode && kind === 'node' && (
          <>
            <div style={{ fontSize: 12 }}>
              Line Number
              <ChangedField change={agentChangeFor('line_no')} onRestore={() => restoreField('line_no')}>
                <div style={{ marginTop: 4 }}>
                  <LineNumberField
                    docId={currentDocument?.id ?? ''}
                    value={values.line_no ?? ''}
                    onChange={(lineNo) => setField('line_no', lineNo)}
                    onSave={() => handleSubmit()}
                    inputRef={lineNumberInputRef}
                  />
                </div>
              </ChangedField>
            </div>

            {/* Line Type / Instrument Line Subtype */}
            <div style={{ display: 'flex', gap: 8 }}>
              <label style={{ fontSize: 12, flex: 1 }}>
                Line Type
                <ChangedField change={agentChangeFor('line_type')} onRestore={() => restoreField('line_type')}>
                  <select
                  value={shownLineType}
                  onChange={(e) => {
                    const next = e.target.value as LineType;
                    setField('line_type', next);
                    if (next !== 'instrument' && values.instrument_line_subtype) {
                      setField('instrument_line_subtype', null);
                    }
                  }}
                  style={{ width: '100%', padding: 8, marginTop: 4, border: '1px solid var(--raven-divider)', borderRadius: 6, fontSize: 12, background: 'white' }}
                >
                  {(Object.keys(LINE_TYPE_LABELS) as LineType[]).map((key) => (
                    <option key={key} value={key}>{LINE_TYPE_LABELS[key]}</option>
                  ))}
                </select>
                </ChangedField>
              </label>
              {shownLineType === 'instrument' && (
                <label style={{ fontSize: 12, flex: 1 }}>
                  Subtype
                  <ChangedField change={agentChangeFor('instrument_line_subtype')} onRestore={() => restoreField('instrument_line_subtype')}>
                    <select
                    value={(values.instrument_line_subtype as InstrumentLineSubtype | null | undefined) ?? ''}
                    onChange={(e) => {
                      const v = e.target.value;
                      setField('instrument_line_subtype', v ? (v as InstrumentLineSubtype) : null);
                    }}
                    style={{ width: '100%', padding: 8, marginTop: 4, border: '1px solid var(--raven-divider)', borderRadius: 6, fontSize: 12, background: 'white' }}
                  >
                    <option value="">Select subtype…</option>
                    {(Object.keys(INSTRUMENT_LINE_SUBTYPE_LABELS) as InstrumentLineSubtype[]).map((key) => (
                      <option key={key} value={key}>{INSTRUMENT_LINE_SUBTYPE_LABELS[key]}</option>
                    ))}
                  </select>
                  </ChangedField>
                </label>
              )}
            </div>

            {renderLineSpecFields()}

            {/* Source Section */}
            <div style={{ padding: 10, background: 'var(--raven-surface-tier2)', border: '1px solid var(--raven-divider)', borderRadius: 6 }}>
              <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--raven-text-secondary)', marginBottom: 8 }}>Source</div>
              <ChangedField change={agentChangeFor('source_ref')} onRestore={() => restoreRef('source_ref', 'source_id')}>
                <RefCombobox
                  label="Source"
                  value={values.source_ref ?? ''}
                  groups={sourceRefGroups}
                  onChange={pickSourceRef}
                  onDraftChange={draftChange('source')}
                  autoFocus={initialLineFocus === 'source'}
                  onHighlightEntity={onHighlightEntity}
                />
              </ChangedField>
              {renderEndSheetField('source')}
            </div>

            {/* Destination Section */}
            <div style={{ padding: 10, background: 'var(--raven-surface-tier2)', border: '1px solid var(--raven-divider)', borderRadius: 6 }}>
              <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--raven-text-secondary)', marginBottom: 8 }}>Destination</div>
              <ChangedField change={agentChangeFor('dest_ref')} onRestore={() => restoreRef('dest_ref', 'dest_id')}>
                <RefCombobox
                  label="Destination"
                  value={values.dest_ref ?? ''}
                  groups={destRefGroups}
                  onChange={pickDestRef}
                  onDraftChange={draftChange('destination')}
                  autoFocus={initialLineFocus === 'destination'}
                  onHighlightEntity={onHighlightEntity}
                />
              </ChangedField>
              {renderEndSheetField('destination')}
            </div>

            {/* Drain Type display */}
            {values.drain_type && (
              <div style={{
                padding: 10,
                background: 'var(--raven-warning-bg)',
                border: '1px solid var(--raven-warning-border)',
                borderRadius: 6
              }}>
                <div style={{ fontSize: 12 }}>
                  <span style={{ fontWeight: 600, color: 'var(--raven-warning-text)' }}>Drain Type: </span>
                  <span>{values.drain_type} — {DRAIN_TYPES[values.drain_type as keyof typeof DRAIN_TYPES] || values.drain_type}</span>
                </div>
              </div>
            )}

            {/* === Line Properties (collapsible) === */}
            <div style={{ border: '1px solid var(--raven-divider)', borderRadius: 6 }}>
              <button
                type="button"
                onClick={() => setLinePropsExpanded((v) => !v)}
                style={{
                  width: '100%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  padding: '8px 10px',
                  background: 'var(--raven-surface-tier2)',
                  border: 'none',
                  borderRadius: 6,
                  cursor: 'pointer',
                  fontSize: 12,
                  fontWeight: 600,
                  color: 'var(--raven-text-primary)',
                }}
              >
                <span>Line Properties</span>
                <span style={{ color: 'var(--raven-text-secondary)' }}>{linePropsExpanded ? '▾' : '▸'}</span>
              </button>
              {linePropsExpanded && (
                <div style={{ padding: 10, display: 'flex', flexDirection: 'column', gap: 8, borderTop: '1px solid var(--raven-divider)' }}>
                  <div style={{ display: 'flex', gap: 8, alignItems: 'flex-end' }}>
                    <label style={{ fontSize: 12, flex: 1 }}>
                      Installation Type
                      <ChangedField change={agentChangeFor('installation_type')} onRestore={() => restoreField('installation_type')}>
                        <select
                        value={(values.installation_type as InstallationType | null | undefined) ?? ''}
                        onChange={(e) => {
                          const v = e.target.value;
                          setField('installation_type', v ? (v as InstallationType) : null);
                        }}
                        style={{ width: '100%', padding: 8, marginTop: 4, border: '1px solid var(--raven-divider)', borderRadius: 6, fontSize: 12, background: 'white' }}
                      >
                        <option value="">None</option>
                        {(Object.keys(INSTALLATION_TYPE_LABELS) as InstallationType[]).map((key) => (
                          <option key={key} value={key}>{INSTALLATION_TYPE_LABELS[key]}</option>
                        ))}
                      </select>
                      </ChangedField>
                    </label>
                    <label style={{ fontSize: 12, display: 'flex', alignItems: 'center', gap: 6, paddingBottom: 8, whiteSpace: 'nowrap' }}>
                      <ChangedField change={agentChangeFor('is_jacketed')} onRestore={() => restoreField('is_jacketed')}>
                        <input
                        type="checkbox"
                        checked={Boolean(values.is_jacketed)}
                        onChange={(e) => setField('is_jacketed', e.target.checked)}
                      />
                      </ChangedField>
                      Jacketed
                    </label>
                  </div>
                  <label style={{ fontSize: 12 }}>
                    Tracing
                    <ChangedField change={agentChangeFor('tracing_type')} onRestore={() => restoreField('tracing_type')}>
                      <select
                      value={(values.tracing_type as TracingType | null | undefined) ?? ''}
                      onChange={(e) => {
                        const v = e.target.value;
                        setField('tracing_type', v ? (v as TracingType) : null);
                      }}
                      style={{ width: '100%', padding: 8, marginTop: 4, border: '1px solid var(--raven-divider)', borderRadius: 6, fontSize: 12, background: 'white' }}
                    >
                      <option value="">None</option>
                      {(Object.keys(TRACING_TYPE_LABELS) as TracingType[]).map((key) => (
                        <option key={key} value={key}>{key === 'none' ? 'No Tracing' : TRACING_TYPE_LABELS[key]}</option>
                      ))}
                    </select>
                    </ChangedField>
                  </label>

                  <div style={{ display: 'flex', gap: 8 }}>
                    <label style={{ fontSize: 12, flex: 1 }}>
                      Flow Profile
                      <ChangedField change={agentChangeFor('flow_profile')} onRestore={() => restoreField('flow_profile')}>
                        <select
                        value={(values.flow_profile as FlowProfile | null | undefined) ?? ''}
                        onChange={(e) => {
                          const next = e.target.value ? (e.target.value as FlowProfile) : null;
                          setField('flow_profile', next);
                          if (next !== 'sloped' && values.slope_ratio) {
                            setField('slope_ratio', null);
                          }
                        }}
                        style={{ width: '100%', padding: 8, marginTop: 4, border: '1px solid var(--raven-divider)', borderRadius: 6, fontSize: 12, background: 'white' }}
                      >
                        <option value="">None</option>
                        {(Object.keys(FLOW_PROFILE_LABELS) as FlowProfile[]).map((key) => (
                          <option key={key} value={key}>{FLOW_PROFILE_LABELS[key]}</option>
                        ))}
                      </select>
                      </ChangedField>
                    </label>
                    {values.flow_profile === 'sloped' && (
                      <label style={{ fontSize: 12, flex: 1 }}>
                        Slope (e.g. 1/100)
                        <ChangedField change={agentChangeFor('slope_ratio')} onRestore={() => restoreField('slope_ratio')}>
                          <input
                          type="text"
                          value={values.slope_ratio ?? ''}
                          onChange={(e) => setField('slope_ratio', e.target.value || null)}
                          placeholder="1/100"
                          style={{ width: '100%', padding: 8, marginTop: 4, border: '1px solid var(--raven-divider)', borderRadius: 6, fontSize: 12 }}
                        />
                        </ChangedField>
                      </label>
                    )}
                  </div>
                  <label style={{ fontSize: 12, display: 'flex', alignItems: 'center', gap: 6 }}>
                    <ChangedField change={agentChangeFor('is_two_phase')} onRestore={() => restoreField('is_two_phase')}>
                      <input
                      type="checkbox"
                      checked={Boolean(values.is_two_phase)}
                      onChange={(e) => setField('is_two_phase', e.target.checked)}
                    />
                    </ChangedField>
                    Two-phase flow
                  </label>
                  {values.is_two_phase && (
                    <div style={{
                      padding: '6px 10px',
                      background: 'var(--raven-warning-bg)',
                      border: '1px solid var(--raven-warning-border)',
                      borderRadius: 6,
                      fontSize: 11,
                      color: 'var(--raven-warning-text)',
                    }}>
                      ⚠ Two-phase flow — vibration / support analysis required
                    </div>
                  )}

                  <div style={{ display: 'flex', gap: 8 }}>
                    <label style={{ fontSize: 12, flex: 1 }}>
                      Flow Direction
                      <ChangedField change={agentChangeFor('flow_direction')} onRestore={() => restoreField('flow_direction')}>
                        <select
                        value={(values.flow_direction as FlowDirection | null | undefined) ?? ''}
                        onChange={(e) => {
                          const next = e.target.value ? (e.target.value as FlowDirection) : null;
                          setField('flow_direction', next);
                          if (next !== 'bidirectional_primary' && values.primary_direction) {
                            setField('primary_direction', null);
                          }
                        }}
                        style={{ width: '100%', padding: 8, marginTop: 4, border: '1px solid var(--raven-divider)', borderRadius: 6, fontSize: 12, background: 'white' }}
                      >
                        <option value="">None</option>
                        {(Object.keys(FLOW_DIRECTION_LABELS) as FlowDirection[]).map((key) => (
                          <option key={key} value={key}>{FLOW_DIRECTION_LABELS[key]}</option>
                        ))}
                      </select>
                      </ChangedField>
                    </label>
                    {values.flow_direction === 'bidirectional_primary' && (
                      <label style={{ fontSize: 12, flex: 1 }}>
                        Primary Direction
                        <ChangedField change={agentChangeFor('primary_direction')} onRestore={() => restoreField('primary_direction')}>
                          <select
                          value={(values.primary_direction as PrimaryDirection | null | undefined) ?? ''}
                          onChange={(e) => {
                            const v = e.target.value;
                            setField('primary_direction', v ? (v as PrimaryDirection) : null);
                          }}
                          style={{ width: '100%', padding: 8, marginTop: 4, border: '1px solid var(--raven-divider)', borderRadius: 6, fontSize: 12, background: 'white' }}
                        >
                          <option value="">None</option>
                          {(Object.keys(PRIMARY_DIRECTION_LABELS) as PrimaryDirection[]).map((key) => (
                            <option key={key} value={key}>{PRIMARY_DIRECTION_LABELS[key]}</option>
                          ))}
                        </select>
                        </ChangedField>
                      </label>
                    )}
                  </div>
                  <label style={{ fontSize: 12 }}>
                    Painting specification (optional)
                    <ChangedField change={agentChangeFor('painting_spec')} onRestore={() => restoreField('painting_spec')}>
                      <input
                      value={values.painting_spec ?? ''}
                      onChange={(e) => setField('painting_spec', e.target.value)}
                      placeholder="Painting or coating specification..."
                      disabled={readOnly}
                      style={{ width: '100%', padding: 8, marginTop: 4, border: '1px solid var(--raven-divider)', borderRadius: 6, fontSize: 12 }}
                    />
                    </ChangedField>
                  </label>
                </div>
              )}
            </div>
            <label style={{ fontSize: 12 }}>
              Drawing note (from source)
              <ChangedField change={agentChangeFor('note')} onRestore={() => restoreField('note')}>
                <textarea
                value={values.note ?? ''}
                onChange={(e) => setField('note', e.target.value)}
                placeholder="Note from the source drawing (Excel: NOTE)..."
                rows={2}
                style={{ width: '100%', padding: 8, marginTop: 4, border: '1px solid var(--raven-divider)', borderRadius: 6, resize: 'vertical', fontSize: 12 }}
              />
              </ChangedField>
            </label>
            <label style={{ fontSize: 12 }}>
              Notes (optional)
              <ChangedField change={agentChangeFor('notes')} onRestore={() => restoreField('notes')}>
                <textarea
                value={values.notes ?? ''}
                onChange={(e) => setField('notes', e.target.value)}
                placeholder="Additional notes or metadata..."
                rows={2}
                disabled={readOnly}
                style={{ width: '100%', padding: 8, marginTop: 4, border: '1px solid var(--raven-divider)', borderRadius: 6, resize: 'vertical', fontSize: 12 }}
              />
              </ChangedField>
            </label>
            {renderDrawnPathRow()}

            {/* === Instrument Sequence Management === */}
            {effectiveNode && values._markExistingId && (
              <>
                <div id={findingAnchorId(findingFormId, 'instruments')}
                  style={{ borderTop: '1px solid var(--raven-divider)', marginTop: 4, paddingTop: 10 }}>
                  {/* Branch Candidates */}
                  {branchCandidates.length > 0 && (
                    <div style={{ marginBottom: 12 }}>
                      <div style={{ fontSize: 10, fontWeight: 700, color: 'var(--raven-primary-main)', textTransform: 'uppercase', letterSpacing: '0.4px' }}>
                        Branch Candidates ({branchCandidates.length})
                      </div>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginTop: 6 }}>
                        {branchCandidates.map((candidate) => {
                          const label = candidate.branchLabel || candidate.branchLineNo || `Node #${candidate.branchRef ?? '?'}`;
                          const angle = candidate.branchAngleDeg != null ? `${candidate.branchAngleDeg.toFixed(1)}°` : '—';
                          const dist = candidate.branchDistancePx != null ? `${Math.round(candidate.branchDistancePx)}px` : '—';
                          return (
                            <div
                              key={candidate.id}
                              style={{
                                padding: '6px 8px',
                                borderRadius: 6,
                                border: '1px solid var(--raven-primary-light)',
                                backgroundColor: 'var(--raven-state-hover)',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                                gap: 8,
                              }}
                            >
                              <div style={{ minWidth: 0 }}>
                                <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--raven-text-secondary)' }}>
                                  Branch to {label}
                                </div>
                                <div style={{ fontSize: 9, color: 'var(--raven-text-secondary)' }}>
                                  Angle {angle} · Dist {dist}
                                </div>
                              </div>
                              <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                                <button
                                  type="button"
                                  onClick={() => onConfirmBranchCandidate?.(candidate.id)}
                                  disabled={readOnly}
                                  style={{
                                    padding: '3px 8px',
                                    fontSize: 10,
                                    fontWeight: 600,
                                    color: 'white',
                                    backgroundColor: 'var(--raven-primary-main)',
                                    border: 'none',
                                    borderRadius: 4,
                                    cursor: readOnly ? 'not-allowed' : 'pointer',
                                    opacity: readOnly ? 0.55 : 1,
                                  }}
                                >
                                  Add
                                </button>
                                <button
                                  type="button"
                                  onClick={() => onIgnoreBranchCandidate?.(candidate.id)}
                                  disabled={readOnly}
                                  style={{
                                    padding: '3px 6px',
                                    fontSize: 10,
                                    color: 'var(--raven-text-secondary)',
                                    backgroundColor: 'transparent',
                                    border: '1px solid rgba(0,0,0,0.08)',
                                    borderRadius: 4,
                                    cursor: readOnly ? 'not-allowed' : 'pointer',
                                    opacity: readOnly ? 0.55 : 1,
                                  }}
                                >
                                  Ignore
                                </button>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}

                  {/* Instruments Header */}
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 6 }}>
                    <div style={{ fontSize: 10, fontWeight: 600, color: 'var(--raven-text-secondary)', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                      Instruments ({sortedNodeInstruments.length})
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      {onMarkInstrument && effectiveNode && (
                        <button
                          type="button"
                          onClick={() => onMarkInstrument('node', effectiveNode.id)}
                          disabled={readOnly}
                          style={{
                            padding: '2px 8px',
                            fontSize: 9,
                            fontWeight: 500,
                            color: 'var(--raven-primary-main)',
                            backgroundColor: 'var(--raven-state-selected)',
                            border: '1px solid var(--raven-primary-light)',
                            borderRadius: 4,
                            cursor: readOnly ? 'not-allowed' : 'pointer',
                            opacity: readOnly ? 0.55 : 1,
                            display: 'flex',
                            alignItems: 'center',
                            gap: 3,
                          }}
                          title="Draw an instrument bounding box on the canvas"
                        >
                          + Mark Instrument
                        </button>
                      )}
                      {onAutoAddBranches && effectiveNode?.polyline && (
                        <button
                          type="button"
                          onClick={async () => {
                            setIsDetectingBranches(true);
                            try {
                              await onAutoAddBranches(effectiveNode.id);
                            } finally {
                              setIsDetectingBranches(false);
                            }
                          }}
                          disabled={readOnly || isDetectingBranches}
                          style={{
                            padding: '2px 8px',
                            fontSize: 9,
                            fontWeight: 500,
                            color: isDetectingBranches ? 'var(--raven-text-disabled)' : 'var(--raven-primary-main)',
                            backgroundColor: isDetectingBranches ? 'var(--raven-surface-tier3)' : 'var(--raven-state-selected)',
                            border: `1px solid ${isDetectingBranches ? 'var(--raven-divider)' : 'var(--raven-primary-light)'}`,
                            borderRadius: 4,
                            cursor: readOnly || isDetectingBranches ? 'not-allowed' : 'pointer',
                            opacity: readOnly ? 0.55 : 1,
                            display: 'flex',
                            alignItems: 'center',
                            gap: 3,
                          }}
                          title="Auto-detect intersections with other marked lines"
                        >
                          {isDetectingBranches ? '...' : '\u2935'} Branches
                        </button>
                      )}
                      {onAutoSequence && effectiveNode?.polyline && activeDirectNodeInstruments.some(i => i.bbox) && (
                        <button
                          type="button"
                          onClick={async () => {
                            setIsAutoSequencing(true);
                            try {
                              await onAutoSequence(effectiveNode.id);
                            } finally {
                              setIsAutoSequencing(false);
                            }
                          }}
                          disabled={readOnly || isAutoSequencing}
                          style={{
                            padding: '2px 8px',
                            fontSize: 9,
                            fontWeight: 500,
                            color: isAutoSequencing ? 'var(--raven-text-disabled)' : 'var(--raven-success)',
                            backgroundColor: isAutoSequencing ? 'var(--raven-surface-tier3)' : 'var(--raven-state-selected)',
                            border: `1px solid ${isAutoSequencing ? 'var(--raven-divider)' : 'var(--raven-primary-light)'}`,
                            borderRadius: 4,
                            cursor: readOnly || isAutoSequencing ? 'not-allowed' : 'pointer',
                            opacity: readOnly ? 0.55 : 1,
                            display: 'flex',
                            alignItems: 'center',
                            gap: 3,
                          }}
                          title="Auto-sequence instruments by position along the line"
                        >
                          {isAutoSequencing ? '...' : '\u2195'} Auto-sequence
                        </button>
                      )}
                      {activeDirectNodeInstruments.length > 1 && onReorderInstruments && (
                        <span style={{ fontSize: 9, color: 'var(--raven-text-disabled)' }}>Drag to reorder</span>
                      )}
                    </div>
                  </div>

                  {/* Instrument List */}
                  {sortedNodeInstruments.length === 0 ? (
                    <div style={{
                      padding: '12px 10px',
                      textAlign: 'center',
                      color: 'var(--raven-text-disabled)',
                      fontSize: 12,
                      backgroundColor: 'var(--raven-surface-tier2)',
                      borderRadius: 6,
                    }}>
                      No instruments marked yet.
                      <br />
                      <span style={{ fontSize: 10, color: 'var(--raven-text-secondary)' }}>Draw on canvas to add.</span>
                    </div>
                  ) : (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                      {sortedNodeInstruments.map((inst, index) => {
                        const isUnattachConfirm = confirmUnattach === inst.id;
                        const typeName = inst.instrument_type ? getInstrumentCategoryName(inst.instrument_type) : 'Unknown';
                        const isBranch = Boolean(inst.branch_ref || inst.branch_direction);
                        const displaySequence = inst.sequence_order ?? index + 1;
                        const isDraggingItem = draggedIndex === index;
                        const isDragOver = dragOverIndex === index;
                        const hasBbox = Boolean(inst.bbox);
                        const canReorderInstrument = Boolean(onReorderInstruments)
                          && activeDirectNodeInstruments.length > 1
                          && activeDirectNodeInstrumentIds.has(inst.id);

                        return (
                          <div
                            key={inst.id}
                            draggable={canReorderInstrument}
                            onDragStart={canReorderInstrument ? () => handleInstDragStart(index) : undefined}
                            onDragOver={(e) => handleInstDragOver(e, index)}
                            onDrop={() => handleInstDrop(index)}
                            onDragEnd={handleInstDragEnd}
                            onMouseEnter={() => onHighlightEntity?.(inst.id)}
                            onMouseLeave={() => onHighlightEntity?.(null)}
                            style={{
                              padding: '8px 10px',
                              backgroundColor: isBranch ? 'var(--raven-warning-bg)' : 'var(--raven-surface-tier2)',
                              border: `1px solid ${isBranch ? 'var(--raven-warning-border)' : 'var(--raven-divider)'}`,
                              borderRadius: 6,
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'space-between',
                              gap: 8,
                              opacity: isDraggingItem ? 0.5 : 1,
                              borderTop: isDragOver && draggedIndex !== null && draggedIndex > index
                                ? '2px solid var(--raven-primary-main)'
                                : undefined,
                              borderBottom: isDragOver && draggedIndex !== null && draggedIndex < index
                                ? '2px solid var(--raven-primary-main)'
                                : undefined,
                              cursor: canReorderInstrument ? 'grab' : 'default',
                            }}
                          >
                            {/* Status indicator */}
                            <span
                              style={{
                                width: 6,
                                height: 6,
                                borderRadius: 99,
                                backgroundColor: reviewStatusColor(inst.status, 'instrument'),
                                flexShrink: 0,
                              }}
                              title={inst.status === 'final_and_reviewed' ? 'Reviewed' : hasBbox ? 'Marked' : 'Not marked'}
                            />

                            {/* Drag handle */}
                            {activeDirectNodeInstruments.length > 1 && onReorderInstruments && (
                              <span
                                style={{
                                  color: 'var(--raven-text-disabled)',
                                  fontSize: 12,
                                  userSelect: 'none',
                                  cursor: canReorderInstrument ? 'grab' : 'default',
                                  opacity: canReorderInstrument ? 1 : 0.35,
                                }}
                                title={canReorderInstrument ? 'Drag to reorder' : 'Instrument-attached children are not reordered with node instruments'}
                              >
                                {'\u2807'}
                              </span>
                            )}

                            <div style={{ flex: 1, minWidth: 0 }}>
                              <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                                <span style={{
                                  width: 16,
                                  height: 16,
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  backgroundColor: isBranch ? 'var(--raven-warning-bg)' : 'var(--raven-surface-tier2)',
                                  color: isBranch ? 'var(--raven-warning-text)' : 'var(--raven-text-secondary)',
                                  borderRadius: 3,
                                  fontSize: 9,
                                  fontWeight: 600,
                                }}>
                                  {displaySequence}
                                </span>
                                <span style={{ fontSize: 12, fontWeight: 600, color: 'var(--raven-text-primary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                  {getInstrumentPrimaryLabel(inst)}
                                </span>
                                {isBranch && (
                                  <span
                                    style={{
                                      fontSize: 8,
                                      padding: '1px 4px',
                                      backgroundColor: 'var(--raven-warning-bg)',
                                      color: 'var(--raven-warning-text)',
                                      borderRadius: 3,
                                      fontWeight: 600,
                                    }}
                                  >
                                    BRANCH
                                  </span>
                                )}
                              </div>
                              <div style={{ fontSize: 10, color: 'var(--raven-text-secondary)', marginTop: 1, marginLeft: 20 }}>
                                {typeName}
                                {inst.subtype && <span> · {inst.subtype}</span>}
                                {inst.branch_ref && <span> → #{inst.branch_ref}</span>}
                              </div>
                            </div>

                            {/* Actions: Edit + Unattach */}
                            <div style={{ display: 'flex', gap: 3, flexShrink: 0 }}>
                              {isUnattachConfirm ? (
                                <>
                                  <button
                                    type="button"
                                    onClick={() => {
                                      onUnattachInstrument?.(inst.id);
                                      setConfirmUnattach(null);
                                    }}
                                    disabled={readOnly}
                                    style={{
                                      padding: '3px 8px',
                                      fontSize: 10,
                                      fontWeight: 500,
                                      color: 'white',
                                      backgroundColor: 'var(--raven-error)',
                                      border: 'none',
                                      borderRadius: 3,
                                      cursor: readOnly ? 'not-allowed' : 'pointer',
                                      opacity: readOnly ? 0.55 : 1,
                                    }}
                                  >
                                    Confirm
                                  </button>
                                  <button
                                    type="button"
                                    onClick={() => setConfirmUnattach(null)}
                                    style={{
                                      padding: '3px 8px',
                                      fontSize: 10,
                                      fontWeight: 500,
                                      color: 'var(--raven-text-secondary)',
                                      backgroundColor: 'white',
                                      border: '1px solid var(--raven-divider)',
                                      borderRadius: 3,
                                      cursor: 'pointer',
                                    }}
                                  >
                                    {'\u2715'}
                                  </button>
                                </>
                              ) : (
                                <>
                                  <button
                                    type="button"
                                    onClick={() => onEditInstrument?.(inst.id)}
                                    disabled={readOnly}
                                    style={rowEditButtonStyle(readOnly)}
                                  >
                                    Edit
                                  </button>
                                  <button
                                    type="button"
                                    onClick={() => setConfirmUnattach(inst.id)}
                                    disabled={readOnly}
                                    style={{
                                      padding: '3px 6px',
                                      fontSize: 10,
                                      color: 'var(--raven-text-disabled)',
                                      backgroundColor: 'transparent',
                                      border: '1px solid var(--raven-divider)',
                                      borderRadius: 3,
                                      cursor: readOnly ? 'not-allowed' : 'pointer',
                                      opacity: readOnly ? 0.55 : 1,
                                    }}
                                    title="Unattach from node"
                                  >
                                    {'\u2715'}
                                  </button>
                                </>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                  {renderFieldFindings('instruments')}
                </div>
              </>
            )}
          </>
        )}

        {/* In both diagram modes: the rail marks a P&ID line and a PFD stream alike. */}
        <FindingsList findings={shownFindings}
          changedElements={changedElementFindings(draft, findingElements)}
          formId={findingFormId}
          isValidating={draftValidating} />

        {formLevelError && (
          <div role="alert" style={{ padding: '8px 10px', fontSize: 11, color: 'var(--raven-error-text)', background: 'var(--raven-error-bg)', borderRadius: 8 }}>
            {formLevelError}
          </div>
        )}

        <DockFooter>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flex: 1 }}>
          {/* Left side: Delete and Redraw for a saved element */}
          <div style={{ display: 'flex', gap: 4 }}>
            {isMarkingExisting && onDelete && !readOnly && (
              <button type="button" onClick={onDelete} style={footerDangerButtonStyle}>
                Delete
              </button>
            )}
            {kind === 'node' && isMarkingExisting && onRedrawPath && (
              <button
                type="button"
                onClick={onRedrawPath}
                disabled={readOnly}
                style={{
                  ...footerDangerButtonStyle,
                  cursor: readOnly ? 'not-allowed' : 'pointer',
                  opacity: readOnly ? 0.55 : 1,
                }}
              >
                Redraw Line
              </button>
            )}
          </div>

          {/* Right side: Cancel and Save */}
          <div style={{ display: 'flex', gap: 8 }}>
            <button
              type="button"
              onClick={onCancel}
              className="pnid-btn pnid-btn--sm pnid-btn--outlined"
            >
              Cancel
            </button>
            <button
              type="submit"
              form={formId}
              disabled={readOnly}
              className="pnid-btn pnid-btn--sm pnid-btn--contained"
            >
              {isMarkingExisting && kind === 'equipment' ? 'Save & Mark' : 'Save'}
            </button>
          </div>
        </div>
        </DockFooter>
      </form>
    </div>
  );
};

export default PnidMarkDialog;
